// ===== Acesso a dados =====
// Todo o tráfego com o banco passa por aqui, via PostgREST sob RLS — o mesmo
// caminho que tests/unit/rls.test.js prova ponta a ponta.
//
// `criarApi(cliente)` recebe o cliente em vez de importá-lo para que o teste
// possa injetar um falso.
//
// ATENÇÃO: store.jsx NÃO pode importar este arquivo (nem supabase.js). O
// acesso chega ao store como prop vinda do app.jsx — importar aqui instancia
// o cliente no escopo do módulo e quebra o teste de contrato dos 44 arquivos.

// Dinheiro que ENTRA e SAI do banco passa por aqui. numeric(14,2) ja e exato do
// lado do Postgres; o risco mora no JavaScript dos dois lados da conversa.
import { dinheiro } from '../dinheiro.js';
import { nomeDaModalidade } from '../../supabase/functions/_shared/modalidade-transporte.js';
import { BUCKET_ANEXOS, caminhoDoAnexo, mapearAnexo, tipoDoArquivo, validarArquivo } from '../anexos.js';

function conferir({ data, error }) {
  // Erro do banco vira exceção. Devolver [] silenciosamente faria uma falha de
  // permissão parecer "não há registros" — e é exatamente o tipo de engano que
  // custou caro neste projeto.
  //
  // code/details/hint do PostgREST são copiados para o Error em vez de
  // descartados: distinguir 42501 (RLS/permissão) de outras falhas já foi
  // decisivo para diagnosticar problema neste projeto, e a mensagem sozinha
  // não carrega isso.
  if (error) {
    const falha = new Error(error.message || 'Falha ao consultar o banco.');
    falha.code = error.code;
    falha.details = error.details;
    falha.hint = error.hint;
    throw falha;
  }
  return data ?? [];
}

// O protótipo (src/data.jsx) montava `originador` em buildOriginador() como
// { tipo: "ia" | "sdr", id, nome }. O banco só guarda tipo/id nessas quatro
// colunas — não existe coluna de nome em `negocios` (não há join aqui, só o
// select('*') de uma tabela). Sem isso o selo do drawer (src/drawer.jsx:1009)
// e o escopo "meus originados" (src/auth.jsx:468) ficam sem originador algum;
// com tipo/id certos eles já funcionam, só o nome falta até algum dia vir de
// uma consulta com join.
// A cadeia do negocio: os elos gravados em `negocio_cadeia` e, quando nenhum
// deles e de ORIGEM, a origem derivada do proprio negocio (originador_* +
// created_at). Reteste do Kaio (25/09): "na aba de cadeia ... nao esta
// registrando quem originou o negocio". Derivar, e nao gravar, porque a politica
// de insert de negocio_cadeia so deixa o DONO gravar -- o SDR que cria para um
// closer seria barrado -- e porque vale tambem para os negocios que ja existem.
//
// Decide pelo TIPO, nao pelo numero. Segundo reteste (25/09): no "testecrm2" o
// repasse de 24/09 foi gravado como elo 1 (a tela da epoca nao contava a
// origem), e "tem elo 1" escondia a origem. A derivada leva seq 0: os gravados
// comecam em 1, e a aba usa seq como chave.
const TIPOS_DE_ORIGEM = new Set(['geracao', 'primeiro_contato']);

export function cadeiaDoNegocio(linha) {
  const gravados = (linha.negocio_cadeia || [])
    .map((e) => ({
      seq: e.seq, tipo: e.tipo, atorTipo: e.ator_tipo === 'ia' ? 'agente' : 'humano',
      atorId: e.ator_user_id ?? e.ator_agente_id ?? null, atorNome: e.ator_nome || null,
      papel: e.papel || null, data: e.ocorreu_em || null,
    }))
    .sort((a, b) => a.seq - b.seq);
  if (gravados.some((e) => TIPOS_DE_ORIGEM.has(e.tipo))) return gravados;
  const ia = linha.originador_tipo === 'ia';
  const atorId = ia ? (linha.originador_agente_id ?? null) : (linha.originador_user_id ?? null);
  if (!atorId) return gravados;
  return [{
    seq: 0, tipo: ia ? 'geracao' : 'primeiro_contato', atorTipo: ia ? 'agente' : 'humano',
    atorId, atorNome: null, papel: null, data: linha.created_at || null,
    meta: { canal: linha.criado_via || null },
  }, ...gravados];
}

function mapearOriginador(linha) {
  const tipo = linha.originador_tipo === 'ia' ? 'ia' : 'sdr';
  const id = tipo === 'ia'
    ? (linha.originador_agente_id ?? linha.agente_id ?? null)
    : (linha.originador_user_id ?? null);
  return { tipo, id, nome: null };
}

// buildRepasse() (src/data.jsx) só calculava repasse para seed.funil ===
// "projetos", porque só esse funil tem canal/especificador. Aqui dentro do
// mapeador o funil ainda não existe — store.jsx só resolve etapa_id → funil
// DEPOIS de chamar mapear() (Task 2, comentário na linha ~97 de store.jsx).
// Não é perda: um negócio sem especificador/parceria não gera repasse de
// qualquer forma, e é exatamente essa checagem abaixo que já filtra os
// negócios do funil lojistas (que nunca têm parceria de canal).
function calcularRepasse(linha, etapa) {
  if (!linha.especificador_id || !linha.parceria_repasse) return null;
  if (linha.perdido) return null;
  const valorRepasse = Math.round((linha.valor || 0) * ((linha.pct_repasse || 0) / 100));
  if (etapa?.isGanho) return { valorRepasse, status: 'Pendente', when: 'há 2 dias' };
  return { valorRepasse, status: 'Estimado', when: null };
}

// ===== Proposta: tela <-> banco (Etapa 3a · Task 1) =====
// Levantamento e decisões: docs/superpowers/specs/2026-08-28-mapeador-proposta-tela-banco.md
// (aprovado pelo Kaio em 28/08, junto com a migration 0041).
//
// A tradução é maior que a de negócios porque a proposta tem itens e porque
// dinheiro passa por aqui: perder `pctDesconto` ou `frete` na volta não dá erro
// nenhum, só muda o total. O teste tests/unit/proposta-mapeador.test.js reexecuta
// a conta da revalidação sobre o objeto mapeado justamente para pegar isso.

// A tela mostra "PRP-2026-0007"; o banco guarda o inteiro 7, vindo da sequence
// (0039). O rótulo é derivado na leitura em vez de gravado porque gravar os dois
// permite que discordem. O ano sai de created_at, não de "hoje": uma proposta de
// dezembro relida em janeiro não pode trocar de número.
function rotuloControle(linha) {
  const ano = new Date(linha.created_at).getFullYear();
  const base = `PRP-${ano}-${String(linha.controle).padStart(4, '0')}`;
  return linha.rev_num > 0 ? `${base} Rev.${linha.rev_num}` : base;
}

// A consulta de proposta, num lugar só: eram quatro cópias da mesma string, e
// acrescentar o embed em três das quatro deixaria a quarta devolvendo proposta
// sem tipo de venda — falha silenciosa, do tamanho exato da que a 0052 conserta.
//
// O aninhamento é resolvido pelo PostgREST a partir da chave estrangeira:
// "The server uses Foreign Keys to determine which database objects can be
// joined together" — https://docs.postgrest.org/en/v12/references/api/resource_embedding.html
// `propostas` tem UMA chave para cada lookup, então não há a ambiguidade que a
// mesma página descreve (duas FKs para a mesma tabela dariam erro).
const SELECT_PROPOSTA =
  '*, proposta_itens(*), tipos_venda(id_tipo_movimentacao_nomus), condicoes_pagamento(id_condicao_nomus)';

function mapearPropostaItem(it) {
  return {
    _id: it.id,
    codigo: it.codigo || '',
    nome: it.produto_nome || '',
    idProduto: it.id_produto_nomus,
    quantidade: Number(it.quantidade),
    unidade: it.unidade || 'un',
    // preco_unitario é o preço CHEIO (0041); o desconto vem separado, senão a
    // revalidação (src/drawer.jsx:832) descontaria duas vezes.
    precoUnitario: dinheiro(it.preco_unitario),
    precoTabela: dinheiro(it.preco_lista),
    pctDesconto: Number(it.pct_desconto),
    totalLinha: dinheiro(it.preco_total),
    precoManual: it.preco_manual,
    // Dois nomes para o mesmo ambiente porque a tela lê pelos dois caminhos:
    // `it.ambiente` no card (src/drawer.jsx:777) e `it.ambienteId` + prop.ambientes
    // no payload Nomus (src/store.jsx:1181). Ambientes não são tabela — o id da
    // tela é efêmero ("amb_"+timestamp) —, então o nome serve de chave.
    ambiente: it.ambiente_nome || null,
    ambienteId: it.ambiente_nome || null,
    // A descrição técnica que o DOCUMENTO imprime (0057). Sem estas duas o
    // documento lê `undefined` e a linha sai sem descrição -- que era o estado
    // até 15/09, com o campo existindo no gerador e não tendo coluna.
    descricaoEstendida: it.descricao_estendida || '',
    dimensoes: it.dimensoes || '',
    medidaCorte: it.medida_corte || '',
    observacoes: it.observacao || '',
  };
}

export function mapearProposta(linha) {
  const itens = (linha.proposta_itens || [])
    .slice()
    .sort((a, b) => a.ordem - b.ordem)
    .map(mapearPropostaItem);

  // Reconstruído a partir dos itens: `ambientes` não tem tabela própria, e o card
  // do drawer precisa da lista para resolver o rótulo pelo caminho antigo.
  const nomes = [...new Set(itens.map((it) => it.ambiente).filter(Boolean))];

  return {
    id: linha.id,
    controle: rotuloControle(linha),
    controleNum: linha.controle,
    nome: linha.nome,
    projeto: linha.projeto || '',
    emissoraId: linha.emissora_codigo || 'pdled',
    // Qual tabela de preco a proposta usou. A coluna existe desde a 0007 e o
    // mapeador nunca a lia -- entao `prop.tabelaId` era sempre undefined e o
    // pedido saia sem `idTabelaPreco`, que e justamente o campo que permite
    // medir qual tabela a PDLED mais usa.
    tabelaId: linha.tabela_id || null,
    itens,
    ambientes: nomes.map((nome) => ({ id: nome, nome })),
    totalProduto: dinheiro(linha.subtotal),
    frete: dinheiro(linha.frete),
    // Margem sobre o frete (0094): INTERNA, parte do `frete` cobrado. Coluna antiga/ausente = 0. Nao vai para a pagina do cliente.
    freteMargem: dinheiro(linha.frete_margem),
    // Modalidade e transportadora do Nomus (0096). NULL = proposta anterior ao registro: a tela pede confirmacao antes do
    // envio ao ERP, em vez de assumir FOB. 0 (CIF) e um valor, nao 'vazio'.
    modalidadeTransporte: inteiroNulavel(linha.modalidade_transporte),
    idPessoaTransportadora: inteiroNulavel(linha.id_pessoa_transportadora),
    modalidadeNome: nomeDaModalidade(linha.modalidade_transporte),
    // Cubagem dos volumes (0095): NULL = nunca calculada, e NULL nao vira 0 (zero afirmaria "cubagem nula").
    metragemCubica: numeroNulavel(linha.metragem_cubica, 4),
    pesoCubado: numeroNulavel(linha.peso_cubado, 2),
    seguro: dinheiro(linha.seguro),
    outrasDespesas: dinheiro(linha.outras_despesas),
    desconto: dinheiro(linha.desconto),
    descontoGlobal: Number(linha.desconto_global_pct),
    total: dinheiro(linha.total),
    // ICMS-ST ESTIMADO (0093). NULL quer dizer "nunca calculado" e NAO vira 0: zero na tela afirmaria que
    // nao ha imposto, e nao sabemos -- o documento mostra "a confirmar".
    icmsStTotal: linha.icms_st_total == null ? null : dinheiro(linha.icms_st_total),
    icmsSt: linha.icms_st ?? null,
    validadeDias: linha.validade_dias,
    validade: linha.validade_dias ? `${linha.validade_dias} dias` : '',
    transportadora: linha.transportadora || '',
    formaPagamento: linha.forma_pagamento || '',
    // O id e o que o ERP precisa; o texto acima e o rotulo de tela. Ate a 0051
    // so o texto era gravado, entao a escolha do vendedor sobrevivia ao reload
    // como palavra e o envio caia no padrao boleto.
    formaPagamentoId: linha.forma_pagamento_id ?? null,
    // Os dois abaixo chegam ANINHADOS, não como coluna: o banco guarda o uuid do
    // lookup e a tela fala o id do Nomus. O embed do PostgREST (SELECT_PROPOSTA)
    // faz a tradução na própria consulta, em vez de obrigar a tela a carregar a
    // tabela inteira só para destraduzir dois números.
    tipoVendaId: linha.tipos_venda?.id_tipo_movimentacao_nomus ?? null,
    condicaoPagamentoId: linha.condicoes_pagamento?.id_condicao_nomus ?? null,
    // Obrigatorio em venda corporativa (store.jsx:1362). Ate a 0053 nao tinha
    // coluna, e por isso NENHUMA venda corporativa -- 12% dos pedidos reais --
    // passava da validacao depois de a proposta ir ao banco.
    pedidoCompraCliente: linha.pedido_compra_cliente || '',
    // O bloco de entrega (0054). `dataFaturamento` e OBRIGATORIA no POST /pedidos --
    // descoberto pelo proprio ERP recusando com regra.campoRequerido. As outras
    // tres vem junto porque sao o mesmo bloco: sem `dataEmissao` o envio usava a
    // data de hoje, e sem o par de prazo o PDF do cliente imprime "A combinar".
    // As duas separadas de proposito: `obs_publica` vai para a NF-e e o cliente
    // le; `obs_interna` nao sai do ERP (0055).
    obsPublica: linha.obs_publica || '',
    obsInterna: linha.obs_interna || '',
    dataFaturamento: linha.data_faturamento || '',
    dataEmissao: linha.data_emissao || '',
    prazoFaturamentoDias: linha.prazo_faturamento_dias ?? null,
    prazoFaturamentoUteis: linha.prazo_faturamento_uteis ?? true,
    condicoes: linha.condicoes || '',
    status: linha.status,
    selecionadaFechamento: linha.selecionada_fechamento,
    revisaoDe: linha.revisao_de,
    revisaoNum: linha.rev_num,
    enviadaEm: linha.enviada_em,
    // Rastreamento do link público (0056). São perguntas diferentes e por isso
    // campos diferentes: `emailEnviadoEm` é quando o e-mail saiu,
    // `vistaPrimeiraEm` é se o cliente chegou a abrir, `vistaUltimaEm` é se ele
    // voltou, e `vistas` é o contador.
    //
    // `vistas` conta TUDO, inclusive varredura de servidor de e-mail — por isso
    // o selo da tela rotula abertura como sinal, não como prova de leitura.
    emailEnviadoEm: linha.email_enviado_em || null,
    emailEnviadoPara: linha.email_enviado_para || '',
    vistaPrimeiraEm: linha.vista_primeira_em || null,
    vistaUltimaEm: linha.vista_ultima_em || null,
    vistas: linha.vistas ?? 0,
    // O token NÃO é devolvido à tela de propósito: quem precisa dele é o e-mail
    // e a função pública. Ter o link no navegador convidaria a copiá-lo de um
    // lugar que não registra que o envio aconteceu.
    temLinkPublico: !!linha.token_publico,
    // O ERP ainda é mock na 3a; numero_cp só é preenchido na 3b.
    erp: { enviado: !!linha.numero_cp, pedidoId: linha.numero_cp || null },
    criadaEm: linha.created_at,
    autorId: linha.autor_id,
  };
}

// Um patch da tela é PARCIAL (a revalidação manda só itens/totalProduto/total,
// src/drawer.jsx:837), então não dá para reaproveitar propostaParaBanco, que monta
// a linha inteira. Só as chaves presentes são traduzidas — mandar `undefined` para
// o PostgREST apagaria a coluna.
const CAMPOS_PROPOSTA = {
  nome: 'nome',
  projeto: 'projeto',
  emissoraId: 'emissora_codigo',
  totalProduto: 'subtotal',
  frete: 'frete',
  freteMargem: 'frete_margem',
  modalidadeTransporte: 'modalidade_transporte',
  idPessoaTransportadora: 'id_pessoa_transportadora',
  metragemCubica: 'metragem_cubica',
  pesoCubado: 'peso_cubado',
  seguro: 'seguro',
  outrasDespesas: 'outras_despesas',
  desconto: 'desconto',
  descontoGlobal: 'desconto_global_pct',
  total: 'total',
  icmsStTotal: 'icms_st_total',
  icmsSt: 'icms_st',
  validadeDias: 'validade_dias',
  tabelaId: 'tabela_id',
  transportadora: 'transportadora',
  formaPagamento: 'forma_pagamento',
  formaPagamentoId: 'forma_pagamento_id',
  pedidoCompraCliente: 'pedido_compra_cliente',
  obsPublica: 'obs_publica',
  obsInterna: 'obs_interna',
  dataFaturamento: 'data_faturamento',
  dataEmissao: 'data_emissao',
  prazoFaturamentoDias: 'prazo_faturamento_dias',
  prazoFaturamentoUteis: 'prazo_faturamento_uteis',
  condicoes: 'condicoes',
  status: 'status',
  selecionadaFechamento: 'selecionada_fechamento',
  // Sem isto gravado, a guarda de idempotencia (Task 3) esquece no F5 e a mesma
  // proposta vira um segundo pedido no ERP.
  enviadaEm: 'enviada_em',
};

// Campos que sao DINHEIRO (nao percentual, nao contagem): passam por dinheiro()
// mesmo que o chamador ja tenha arredondado. Dinheiro nao pode depender da
// disciplina de quem chama -- basta um caminho novo esquecer para voltar o
// centavo quebrado.
const CAMPOS_EM_REAIS = new Set(['totalProduto', 'frete', 'freteMargem', 'seguro', 'outrasDespesas', 'desconto', 'total']);

// Dinheiro em que NULL e um valor legitimo: "nunca calculado" (ICMS-ST, 0093). Passar por dinheiro() sem esta
// distincao transformaria null em 0 -- e a invalidacao do ST (itens mudaram) viraria "sem imposto".
const CAMPOS_EM_REAIS_NULAVEIS = new Set(['icmsStTotal']);

// Numeros em que NULL e legitimo ("nunca calculado") e as casas sao fixas: cubagem em m3 (4) e peso cubado em kg (2, como a
// funcao de cotacao da Jamef). Nao e dinheiro, entao nao passa por dinheiro().
const CAMPOS_NUMERICOS_NULAVEIS = { metragemCubica: 4, pesoCubado: 2 };
// Inteiros em que NULL e legitimo ("nao informado") e 0 e um valor (modalidade 0 = CIF): `x || null` apagaria o CIF.
const CAMPOS_INTEIROS_NULAVEIS = new Set(['modalidadeTransporte', 'idPessoaTransportadora']);
function inteiroNulavel(v) {
  if (v == null || v === '' || !Number.isFinite(Number(v))) return null;
  return Math.trunc(Number(v));
}
function numeroNulavel(v, casas) {
  if (v == null || v === '' || !Number.isFinite(Number(v))) return null;
  return Number(Number(v).toFixed(casas));
}

// Colunas `date` (0054). O input de data devolve STRING VAZIA quando o vendedor
// apaga o campo, e `''` numa coluna date e erro 22007 do Postgres, nao nulo --
// mesma armadilha que `previsao_entrega` tem em negocios.
const CAMPOS_DATA_PROPOSTA = new Set(['dataFaturamento', 'dataEmissao']);

export function propostaPatchParaBanco(patch = {}, lookups = null) {
  const dbPatch = {};
  for (const [telaKey, dbKey] of Object.entries(CAMPOS_PROPOSTA)) {
    if (patch[telaKey] === undefined) continue;
    const bruto = patch[telaKey];
    if (CAMPOS_EM_REAIS.has(telaKey)) dbPatch[dbKey] = dinheiro(bruto);
    else if (CAMPOS_EM_REAIS_NULAVEIS.has(telaKey)) dbPatch[dbKey] = bruto == null ? null : dinheiro(bruto);
    else if (telaKey in CAMPOS_NUMERICOS_NULAVEIS) dbPatch[dbKey] = numeroNulavel(bruto, CAMPOS_NUMERICOS_NULAVEIS[telaKey]);
    else if (CAMPOS_INTEIROS_NULAVEIS.has(telaKey)) dbPatch[dbKey] = inteiroNulavel(bruto);
    else if (CAMPOS_DATA_PROPOSTA.has(telaKey) && bruto === '') dbPatch[dbKey] = null;
    else dbPatch[dbKey] = bruto;
  }
  // Mesma tradução da criação: sem isto, EDITAR uma proposta para trocar o tipo
  // de venda mudaria a tela e não o banco.
  if (lookups) {
    if (patch.tipoVendaId !== undefined) {
      dbPatch.tipo_venda_id = uuidDoLookup(lookups.tiposVenda, patch.tipoVendaId, 'Tipo de venda');
    }
    if (patch.condicaoPagamentoId !== undefined) {
      dbPatch.condicao_pagamento_id = uuidDoLookup(lookups.condicoes, patch.condicaoPagamentoId, 'Condição de pagamento');
    }
  }
  return dbPatch;
}

// Só o que tem coluna. `controle` fica de fora de propósito, para a sequence do
// banco atribuir (0039).
//
// `tipo_venda_id` e `condicao_pagamento_id` entraram na 0052. Até ali o comentário
// aqui dizia que o "bloco Nomus" não persistia na 3a por decisão do Kaio (28/08),
// "montado no envio, na 3b" -- mas a 3b passou e ninguém voltou para montar. Os
// dois são OBRIGATÓRIOS no POST /pedidos e são validados antes do envio
// (store.jsx:1359), então a proposta que ia ao banco voltava impossível de enviar.
// O resto do bloco (parcelas, dataFaturamento, pedidoCompraCliente, fiscal) continua
// de fora — tier 2 e 3 da spec de 11/09.
// Traduz o id do Nomus (o que a tela manipula) no uuid do lookup (o que a coluna
// guarda). LANÇA quando não acha: devolver null aqui repetiria em silêncio o bug
// que esta camada existe para consertar -- a proposta gravaria sem tipo de venda
// e o vendedor só descobriria na hora de mandar ao ERP, com a venda fechada.
// Quem chama (store.createProposta) já tem try/catch com toast.
function uuidDoLookup(lista, idNomus, oQue) {
  if (idNomus == null || idNomus === '') return null;
  const achado = (lista || []).find((x) => Number(x.idNomus) === Number(idNomus));
  if (!achado) {
    throw new Error(
      `${oQue} ${idNomus} não existe no cadastro do CRM. Rode a carga dos lookups do Nomus (migration 0052).`,
    );
  }
  return achado.id;
}

export function propostaParaBanco(daTela, { negocioId, autorId = null, lookups = null } = {}) {
  const ambienteNome = (it) => {
    if (it.ambiente) return it.ambiente;
    if (!it.ambienteId) return null;
    return (daTela.ambientes || []).find((a) => a.id === it.ambienteId)?.nome || null;
  };

  const itens = (daTela.itens || []).map((it, i) => ({
    produto_nome: it.nome || it.produto || '',
    codigo: it.codigo || null,
    id_produto_nomus: it.idProduto ?? null,
    quantidade: Number(it.quantidade) || 0,
    unidade: it.unidade || null,
    preco_lista: dinheiro(it.precoTabela),
    preco_unitario: dinheiro(it.precoUnitario),
    pct_desconto: Number(it.pctDesconto) || 0,
    preco_total: dinheiro(it.totalLinha),
    preco_manual: !!it.precoManual,
    ambiente_nome: ambienteNome(it),
    // CÓPIA, não join (0057). `proposta_itens` já congela `produto_nome`,
    // `codigo` e os preços pela mesma razão: proposta é documento, e o que o
    // cliente recebeu não pode mudar porque alguém editou o catálogo depois.
    descricao_estendida: it.descricaoEstendida || null,
    dimensoes: it.dimensoes || null,
    // `observacao` (0060) guarda o campo `observacoes` do item da tela.
    //
    // `medida_corte`, da mesma migration, NAO e escrita: a medida de corte ja
    // tem casa -- e o campo "Dimensoes (ex: 2,4m)" da tela, que grava em
    // `dimensoes`. Descoberto pelo Kaio em 16/09, olhando a tela: eu tinha
    // criado coluna para um campo que ja existia. Fica orfa ate a 0061 decidir
    // se some ou se vira a casa do dado quando a tela for renomeada.
    observacao: it.observacoes || null,
    ordem: i,
  }));

  const proposta = {
    negocio_id: negocioId,
    tabela_id: daTela.tabelaId || null,
    nome: daTela.nome || 'Proposta',
    projeto: daTela.projeto || null,
    emissora_codigo: daTela.emissoraId || null,
    subtotal: dinheiro(daTela.totalProduto),
    frete: dinheiro(daTela.frete),
    frete_margem: dinheiro(daTela.freteMargem),
    modalidade_transporte: inteiroNulavel(daTela.modalidadeTransporte),
    id_pessoa_transportadora: inteiroNulavel(daTela.idPessoaTransportadora),
    metragem_cubica: numeroNulavel(daTela.metragemCubica, 4),
    peso_cubado: numeroNulavel(daTela.pesoCubado, 2),
    seguro: dinheiro(daTela.seguro),
    outras_despesas: dinheiro(daTela.outrasDespesas),
    desconto: dinheiro(daTela.desconto),
    desconto_global_pct: Number(daTela.descontoGlobal) || 0,
    total: dinheiro(daTela.total),
    // ICMS-ST estimado (0093): ausente grava NULL ("nunca calculado"), nunca 0.
    icms_st_total: daTela.icmsStTotal == null ? null : dinheiro(daTela.icmsStTotal),
    icms_st: daTela.icmsSt ?? null,
    validade_dias: daTela.validadeDias ?? null,
    transportadora: daTela.transportadora || null,
    forma_pagamento: daTela.formaPagamento || null,
    forma_pagamento_id: daTela.formaPagamentoId ?? null,
    pedido_compra_cliente: daTela.pedidoCompraCliente || null,
    // '' vira null: coluna `date` recusa string vazia com 22007, e o rascunho
    // legitimamente nasce sem data.
    obs_publica: daTela.obsPublica || null,
    obs_interna: daTela.obsInterna || null,
    data_faturamento: daTela.dataFaturamento || null,
    data_emissao: daTela.dataEmissao || null,
    prazo_faturamento_dias: daTela.prazoFaturamentoDias ?? null,
    prazo_faturamento_uteis: daTela.prazoFaturamentoUteis ?? null,
    condicoes: daTela.condicoes || null,
    autor_id: autorId,
  };

  // `lookups` ausente = chamada que só quer os itens (store.updateProposta na
  // revalidação de preços). Nesse caminho a proposta nem é usada, então traduzir
  // seria trabalho jogado fora -- e obrigaria todo chamador a carregar as listas.
  if (lookups) {
    proposta.tipo_venda_id = uuidDoLookup(lookups.tiposVenda, daTela.tipoVendaId, 'Tipo de venda');
    proposta.condicao_pagamento_id = uuidDoLookup(lookups.condicoes, daTela.condicaoPagamentoId, 'Condição de pagamento');
  }

  return { proposta, itens };
}

// O banco usa snake_case; as telas, camelCase. A tradução mora aqui, num lugar
// só — espalhar isso pelos componentes seria trocar 91 constantes por 91
// traduções soltas.
// `etapas` chega na forma traduzida da Task 2: `id` e o codigo legivel
// (PROPOSTA), `uuid` e a chave do banco. Por isso a busca e por uuid e o
// stage devolvido e o id.
// ===== Cliente: banco -> tela =====
//
// O QUE ESTE MAPEADOR NÃO FAZ: inventar.
//
// O seed 0006 carregou 2.201 clientes do Nomus com o que o ERP tinha — nome,
// tipo, documento, cidade/uf, e-mail, telefone e `id_pessoa_nomus`. Não trouxe
// pessoa de contato, endereço (a tabela `enderecos` está vazia) nem curva de
// consumo, porque o ERP não devolveu.
//
// Seria fácil derivar um contato do e-mail, como o protótipo fazia com dados
// falsos. Não fazemos: a ficha mostra campo vazio, que é a verdade. Foi
// exatamente por inventar lista plausível que a condição de pagamento ficou
// nove décimos errada até a 0052 — o custo de um dado inventado é que ninguém
// desconfia dele.
//
// `endereco`, `contatos` e `curvaConsumo` saem com a FORMA certa e vazios: a
// tela faz `cliente.endereco.cep` e `cliente.contatos.map(...)` em vários
// pontos, e devolver `null` trocaria campo vazio por tela quebrada.
// A tela junta "Cidade · UF" num campo so (herdado dos seeds) e o banco guarda
// separado, que e o certo. Estas duas funcoes sao a fronteira -- sem elas,
// gravar de volta poria "Belo Horizonte · MG" dentro de `cidade`.
export function partirCidadeUf(texto) {
  const partes = String(texto || '').split('·').map((p) => p.trim()).filter(Boolean);
  if (partes.length >= 2) return { cidade: partes[0], uf: partes[1].slice(0, 2).toUpperCase() };
  return { cidade: partes[0] || null, uf: null };
}

function enderecoDaLinha(linha) {
  const lista = linha.enderecos || [];
  const e = lista.find((x) => x.principal) || lista.find((x) => x.tipo === 'faturamento') || lista[0] || {};
  return {
    cep: e.cep || '',
    rua: e.logradouro || '',
    num: e.numero || '',
    complemento: e.complemento || '',
    bairro: e.bairro || '',
    // Cai para o que esta em `clientes` quando nao ha endereco cadastrado: a
    // carteira importada tem cidade/UF la e endereco nenhum aqui.
    cidade: e.cidade || linha.cidade || '',
    uf: e.uf || linha.uf || '',
  };
}

export function mapearCliente(linha) {
  const ultima = linha.ultima_compra_em ? new Date(linha.ultima_compra_em) : null;
  const lastBuyDias = ultima
    ? Math.max(0, Math.floor((Date.now() - ultima.getTime()) / 86400000))
    : null;

  return {
    id: linha.id,
    nome: linha.nome,
    // O ERP não trouxe pessoa de contato para esta carteira; fica vazio.
    contato: linha.contato || '',
    cargo: '',
    email: linha.email || '',
    telefone: linha.telefone || '',
    // A tela mostra "Cidade · UF" num campo só (era assim nos seeds); o banco
    // guarda separado, que é o certo. A junção é de exibição.
    cidade: [linha.cidade, linha.uf].filter(Boolean).join(' · '),
    tipo: linha.tipo,
    tipoDoc: linha.documento_tipo,
    cnpj: linha.cnpj || '',
    cpf: linha.cpf || '',
    ie: linha.inscricao_estadual || '',
    // Importados do Nomus em 16/09 (seed 0012c). `razaoSocial` e distinta de
    // `nome`: quem emite NF-e precisa dela, e ate entao os dois papeis moravam
    // no mesmo campo.
    razaoSocial: linha.razao_social || '',
    // 1 contribuinte · 2 isento de inscricao · 3 nao contribuinte (indIEDest da
    // NF-e). Antes era INFERIDO da IE, e a inferencia nunca produzia o 2 -- ha
    // 99 clientes assim. Decide o ICMS interestadual.
    tipoContribuinte: linha.tipo_contribuinte_icms ?? null,
    // `idPessoaCliente` do POST /pedidos (0043). Sem ele o envio ao ERP barra —
    // é o campo por causa do qual esta função existe.
    erpId: linha.id_pessoa_nomus ?? null,
    status: linha.status,
    faturamento: Number(linha.faturamento_total) || 0,
    ultimaCompraEm: linha.ultima_compra_em || null,
    lastBuyDias,
    lastBuy: lastBuyDias == null ? '—' : `${lastBuyDias} dias`,
    dealsAtivos: linha.deals_ativos || 0,
    dealsTotal: linha.deals_total || 0,
    alertasPend: 0,
    tabelaId: linha.tabela_id || null,
    nichoId: linha.nicho_id || null,
    // A tela usa o CODIGO do nicho ("varejo"); a coluna guarda o uuid. Este mapeador
    // e puro e sincrono, entao nao le o banco: `nicho_codigo` chega pronto, posto
    // por `listarClientes`/`criarCliente`/`atualizarCliente` (que leem `nichos`).
    // Linha que nao passou por la fica sem nicho na tela, e nao com um nicho errado.
    nicho: linha.nicho_codigo ?? null,
    canalId: linha.canal_id || null,
    isCanal: !!linha.is_canal,
    canalTipo: linha.canal_tipo || null,
    canalResponsavel: linha.canal_responsavel || null,
    canalAtivo: linha.canal_ativo,
    canalInicio: linha.canal_inicio || null,
    parceriaRepasse: !!linha.parceria_repasse,
    pctRepasse: Number(linha.pct_repasse) || 0,
    // Indicacao de reativacao (0103), gravada por carga: so LEITURA. O `authenticated`
    // nao tem GRANT de escrita nestas colunas, entao elas ficam de fora de
    // CAMPOS_CLIENTE de proposito -- um insert/update que as levasse daria
    // "permission denied" no cadastro inteiro.
    reativacao: linha.reativacao_motivo
      ? { motivo: linha.reativacao_motivo, em: linha.reativacao_em || null }
      : null,
    // Forma certa, conteúdo vazio — ver nota acima.
    // O endereco vem de `enderecos` (0005), aninhado pela chave estrangeira.
    // Ate 16/09 a tabela estava vazia e isto saia sempre vazio -- o formulario
    // pedia logradouro, numero, bairro e CEP e descartava tudo.
    endereco: enderecoDaLinha(linha),
    enderecoEntregaMesmo: true,
    enderecoEntrega: null,
    contatos: [],
    curvaConsumo: [],
    emailFinanceiro: '',
    condPagamento: '',
    ownerId: null,
    // Quem cadastrou pelo CRM (0086). Nulo nas cargas e no Nomus.
    criadoPor: linha.criado_por ?? null,
    criadoEm: linha.created_at,
  };
}

// ===== Tabela de preço: banco -> tela =====
//
// `fator` e o multiplicador sobre o preco BASE (0058): CORP 45 = base x 1,45.
// Guardar o fator em vez dos precos e o que impede a margem defasar -- cinco das
// nove CORP estavam calculadas sobre a base anterior e ninguem tinha percebido.
export function mapearTabelaPreco(linha) {
  return {
    id: linha.id,
    codigo: linha.codigo,
    nome: linha.nome,
    descricao: linha.descricao || '',
    tipo: linha.tipo || null,          // base | fator | manual
    fator: linha.fator == null ? null : Number(linha.fator),
    idNomus: linha.id_tabela_preco_nomus ?? null,
    // `descontoBase` e da epoca em que a tela inventava desconto por tabela. As
    // reais tem 0 -- conferido nas 23. Fica exposto porque a tela de Ajustes o
    // desenha; quem manda no preco e o `fator`.
    descontoBase: Number(linha.desconto_base) || 0,
    cor: linha.cor || 'var(--color-accent)',
    ativo: linha.ativo !== false,
    itens: [],
  };
}

// ===== Notificação: banco -> sino =====
//
// O sino esperava um formato que só existia em memória (`when: "agora"`,
// `contexto`, `userNome`). Traduzir aqui é o que permite a notificação vir do
// banco sem reescrever a tela.
export function quandoRelativo(iso, agora = new Date()) {
  if (!iso) return '';
  const min = (agora.getTime() - new Date(iso).getTime()) / 60000;
  // Relógio do navegador adiantado faria "há -3 min". Melhor "agora".
  if (min < 1) return 'agora';
  if (min < 60) return `há ${Math.floor(min)} min`;
  if (min < 1440) return `há ${Math.floor(min / 60)}h`;
  const d = Math.floor(min / 1440);
  return `há ${d} dia${d > 1 ? 's' : ''}`;
}

export function mapearNotificacao(linha, nomePorId = {}) {
  return {
    id: linha.id,
    userId: linha.destinatario_id,
    userNome: nomePorId[linha.destinatario_id] || '—',
    tipo: linha.tipo,
    titulo: linha.titulo,
    contexto: linha.texto || '',
    // `origem_id` só vira link de negócio quando a origem É um negócio. A
    // notificação da proposta aberta (função `proposta-publica`) grava
    // `origem_tipo = 'proposta'`, e usar esse id como `leadId` faria o sino
    // abrir um negócio que não existe.
    leadId: linha.origem_tipo === 'negocio' ? linha.origem_id : null,
    origemTipo: linha.origem_tipo || null,
    origemId: linha.origem_id || null,
    when: quandoRelativo(linha.created_at),
    criadoEm: linha.created_at,
    lida: !!linha.lida,
  };
}

// ===== Cliente: tela -> banco =====
//
// Ate 16/09 nao existia: `updateCliente` era `setClientes(...)` e ponto, entao
// editar cliente nao sobrevivia ao F5 -- nem o endereco, nem a inscricao
// estadual, nem a razao social. O formulario aceitava, mostrava salvo, e o
// reload desfazia.
//
// Patch PARCIAL: so as chaves presentes viajam. Mandar `undefined` para o
// PostgREST apagaria a coluna, e a tela manda patch de um campo so em varios
// lugares.
const CAMPOS_CLIENTE = {
  nome: 'nome',
  razaoSocial: 'razao_social',
  tipo: 'tipo',
  tipoDoc: 'documento_tipo',
  cnpj: 'cnpj',
  cpf: 'cpf',
  ie: 'inscricao_estadual',
  tipoContribuinte: 'tipo_contribuinte_icms',
  email: 'email',
  telefone: 'telefone',
  contato: 'contato',
  status: 'status',
  tabelaId: 'tabela_id',
  nichoId: 'nicho_id',
  canalId: 'canal_id',
  erpId: 'id_pessoa_nomus',
  // Os campos de CANAL faltavam aqui. O mapeador de volta ja os lia (is_canal,
  // canal_tipo, parceria_repasse...), entao a ida e a volta estavam
  // assimetricas: a tela mostrava um canal depois do reload so se ele tivesse
  // sido criado por outro caminho. Marcar um cliente como canal pela ficha nao
  // sobrevivia ao F5.
  isCanal: 'is_canal',
  canalTipo: 'canal_tipo',
  canalResponsavel: 'canal_responsavel',
  canalAtivo: 'canal_ativo',
  parceriaRepasse: 'parceria_repasse',
  // A % do canal. Ja saiu deste mapa uma vez (30/09): a 0038 a trancou para
  // `authenticated` e `createCliente` a mandava como 0 em TODO cadastro, o que
  // derrubava o INSERT inteiro -- nenhum cliente era criado pela tela. Voltou com a
  // 0085 (decisao do Kaio: "o financeiro define o repasse"): o BANCO a confere com
  // um gatilho que so olha a MUDANCA e a permissao `clientes.definir_repasse`. A
  // tela so a manda quando quem define a alterou (cliente-page.jsx) e nunca no
  // cadastro (store.jsx), mas o mapa ja nao pode repetir aquele erro.
  pctRepasse: 'pct_repasse',
  // `razaoSocial` e `tipoContribuinte` ganharam permissao de gravacao na 0084;
  // `erpId` (id_pessoa_nomus) continua SO do servidor (Edge Function criar-cliente-nomus).
};

const CHAVE_NICHO_PARA_RESOLVER = 'nicho_para_resolver';

export function clientePatchParaBanco(daTela = {}) {
  const patch = {};
  for (const [tela, coluna] of Object.entries(CAMPOS_CLIENTE)) {
    if (daTela[tela] === undefined) continue;
    // String vazia vira null; booleano e numero passam intactos. Sem a
    // distincao, `pctRepasse: 0` continuaria 0 (certo), mas um dia alguem
    // mandaria `isCanal: false` e a regra do '' nao o alcancaria -- o risco real
    // e o inverso: `null` numa coluna NOT NULL como `parceria_repasse`.
    const v = daTela[tela];
    patch[coluna] = (typeof v === 'string' && v === '') ? null : v;
  }
  // `nicho` e o CODIGO ("varejo"); a coluna `nicho_id` e uuid. Esta funcao e pura e
  // sincrona, entao nao converte: so deixa o codigo passar numa chave INTERNA, que
  // `criarCliente`/`atualizarCliente` trocam por `nicho_id` antes de gravar e que
  // nunca chega ao banco (coluna inexistente). A chave nao se chama `nicho_codigo`
  // de proposito: esse e o nome do que a LEITURA devolve, e os dubles de teste
  // devolvem o patch dentro da linha -- o eco esconderia uma conversao quebrada.
  // `undefined` = "nao mexa no nicho"; `''` e `null` = apagar.
  if (daTela.nicho !== undefined) patch[CHAVE_NICHO_PARA_RESOLVER] = daTela.nicho || null;
  // "Cidade · UF" e campo de EXIBICAO. Sem partir aqui, o banco receberia a
  // string inteira dentro de `cidade` e a UF sumiria -- e e a UF que decide o
  // ICMS interestadual.
  if (daTela.cidade !== undefined) {
    const { cidade, uf } = partirCidadeUf(daTela.cidade);
    patch.cidade = cidade;
    if (uf) patch.uf = uf;
  }
  // O endereco tem tabela propria; quem grava e `atualizarCliente`.
  if (daTela.endereco?.uf) patch.uf = String(daTela.endereco.uf).slice(0, 2).toUpperCase();
  if (daTela.endereco?.cidade) patch.cidade = daTela.endereco.cidade;
  // `ie` vazio vira null, e nao a palavra "isento": a coluna e texto livre e
  // "isento" ali viraria uma inscricao estadual chamada isento.
  if (patch.inscricao_estadual === 'isento') patch.inscricao_estadual = null;
  return patch;
}

export function enderecoParaBanco(clienteId, endereco = {}, tipo = 'faturamento') {
  return {
    cliente_id: clienteId,
    tipo,
    cep: endereco.cep || null,
    logradouro: endereco.rua || null,
    numero: endereco.num || null,
    complemento: endereco.complemento || null,
    bairro: endereco.bairro || null,
    cidade: endereco.cidade || null,
    uf: endereco.uf ? String(endereco.uf).slice(0, 2).toUpperCase() : null,
    principal: tipo === 'faturamento',
  };
}

// ===== Produto: banco -> tela =====
//
// Mesma história dos clientes: `PRODUTOS` em src/data.jsx era `[]` desde o commit
// inicial e não havia `listarProdutos`. Os 12.723 produtos carregados do Nomus
// nunca chegaram à tela — e sem produto não se monta proposta nenhuma.
//
// `idProduto` é o campo que o ERP exige em cada item do POST /pedidos; sem ele o
// envio barra com "item sem idProduto Nomus".
export function mapearProduto(linha) {
  const un = linha.unidade_id;
  return {
    id: linha.id,
    codigo: linha.codigo || linha.sku || '',
    nome: linha.nome,
    // `descricao` e a do NOMUS -- o Kaio a recusou em 14/09 ("nao quero a
    // descricao do nomus"). Fica exposta porque o catalogo a mostra, mas nao e
    // ela que vai para a proposta.
    descricao: linha.descricao || '',
    // A descricao TECNICA, escrita por nos (0057). E esta que o documento
    // imprime, copiada para o item no momento da proposta.
    descricaoEstendida: linha.descricao_estendida || '',
    dimensoes: linha.dimensoes || '',
    idProduto: linha.id_produto_nomus ?? null,
    idUnidadeMedida: un ?? 4,
    // A tela rotula só estes dois casos (proposta.jsx:136); o resto cai em "un".
    unidade: un === 2 ? 'm' : 'un',
    // `preco_lista` está zerado para todo o catálogo: a carga do Nomus não trouxe
    // tabela de preço. É pendência conhecida, e por isso o vendedor digita o
    // preço na proposta. Zero aqui é o valor real da coluna, não um palpite.
    precoTabela: Number(linha.preco_lista) || 0,
    categoria: linha.categoria || null,
    // O filtro do catálogo agrupa por NOME do grupo (proposta.jsx:87); a coluna
    // guarda uuid, então o nome vem aninhado pela chave estrangeira.
    grupo: linha.grupos?.nome || null,
    status: linha.status,
    pesoKg: linha.peso_kg ?? null,
    ncm: linha.ncm || '',
    origemProduto: linha.origem_produto ?? null,
  };
}

// So as colunas que o CRM pode editar (0070: grant de UPDATE por coluna).
// Mandar qualquer outra faria o banco recusar a gravacao INTEIRA com 42501.
const PRODUTO_EDITAVEL = { descricaoEstendida: 'descricao_estendida', dimensoes: 'dimensoes', pesoKg: 'peso_kg' };

export function produtoPatchParaBanco(patch = {}) {
  const db = {};
  for (const [daTela, coluna] of Object.entries(PRODUTO_EDITAVEL)) {
    if (!(daTela in patch)) continue;
    const v = patch[daTela];
    if (daTela === 'pesoKg') db[coluna] = v === '' || v == null ? null : Number(v);
    else db[coluna] = typeof v === 'string' ? (v.trim() || null) : (v ?? null);
  }
  return db;
}

export function mapearNegocio(linha, etapas = []) {
  const etapa = etapas.find((e) => e.uuid === linha.etapa_id) || null;
  return {
    id: linha.id,
    empresa: linha.empresa,
    contato: linha.contato,
    cargo: linha.cargo,
    contatoDecisor: !!linha.contato_decisor,
    email: linha.email,
    telefone: linha.telefone,
    segmento: linha.segmento,
    valor: linha.valor,
    stage: etapa ? etapa.id : null,
    calor: linha.calor,
    origem: linha.origem,
    ownerId: linha.owner_id,
    clienteId: linha.cliente_id,
    // Tabela de preco FIXA do negocio (0077). Nula = a do cliente.
    tabelaId: linha.tabela_id || null,
    especificadorId: linha.especificador_id,
    agenteId: linha.agente_id,
    tipoCliente: linha.tipo_cliente,
    bantScore: linha.bant_score,
    diasNoEstagio: linha.dias_no_estagio,
    // `null` vira "—" AQUI, na entrada, e nao em cada tela. Tres views testam
    // `proximaAcao !== "—"` para decidir se desenham o chip de proxima acao
    // (app.jsx:540, report-details.jsx:282, kanban.jsx:112) -- com `null` a
    // comparacao da verdade e o chip aparece vazio, dizendo que ha uma acao
    // combinada que ninguem escreveu.
    proximaAcao: linha.proxima_acao || "—",
    proximaDue: linha.proxima_due,
    ultimoMovimento: linha.ultimo_movimento_em,
    // Data em que o negocio foi ganho (gatilho no banco, migration 0064). Os
    // relatorios filtram o mes por ela. `null` = ainda nao ganho.
    ganhoEm: linha.ganho_em || null,
    // Quando foi perdido (gatilho, migration 0065). A coorte usa. `null` com
    // perdido=true = perda anterior a 0065, sem data real.
    perdidoEm: linha.perdido_em || null,
    perdido: !!linha.perdido,
    motivoPerda: linha.motivo_perda_id,
    obsPerda: linha.obs_perda,
    scoreFit: linha.score_fit,
    fonteCadeia: linha.fonte_cadeia,
    pedidoNomus: linha.pedido_nomus,
    previsaoEntrega: linha.previsao_entrega,
    parceriaRepasse: linha.parceria_repasse,
    pctRepasse: linha.pct_repasse,
    externalId: linha.external_id,
    // string ISO do banco → Date: o filtro de período do Pipeline
    // (src/app.jsx:418) faz `l.criadoData < cutoff` contra um Date. Comparar
    // string com Date dá NaN e o filtro nunca exclui nada — silenciosamente.
    criadoData: linha.created_at ? new Date(linha.created_at) : null,
    originador: mapearOriginador(linha),
    repasse: calcularRepasse(linha, etapa),
    funil: null,           // preenchido pelo store, que conhece o mapa de funis
    tags: [],
    cadeia: cadeiaDoNegocio(linha),
    bant: {},
  };
}

// O caminho de volta: tela -> banco, para o PATCH de um negócio.
//
// Até 14/09 não existia: `store.updateLead` só mexia no array em memória, e três
// comentários espalhados pelo projeto avisavam disso (drawer.jsx:902,
// modals.jsx:942). Tudo que passava por lá — vínculo de cliente, canal, repasse,
// número do pedido, data prometida — morria no F5.
//
// Todas as chaves abaixo JÁ tinham coluna em `negocios` (0006:9-68); não faltava
// schema, faltava o fio.
const CAMPOS_NEGOCIO = {
  empresa: 'empresa',
  contato: 'contato',
  contatoDecisor: 'contato_decisor',
  cargo: 'cargo',
  email: 'email',
  telefone: 'telefone',
  segmento: 'segmento',
  valor: 'valor',
  calor: 'calor',
  ownerId: 'owner_id',
  clienteId: 'cliente_id',
  especificadorId: 'especificador_id',
  tipoCliente: 'tipo_cliente',
  proximaAcao: 'proxima_acao',
  proximaDue: 'proxima_due',
  pedidoNomus: 'pedido_nomus',
  previsaoEntrega: 'previsao_entrega',
  parceriaRepasse: 'parceria_repasse',
  pctRepasse: 'pct_repasse',
  scoreFit: 'score_fit',
  tabelaId: 'tabela_id',
};

// Colunas `date`. O input de data devolve STRING VAZIA quando o usuário limpa o
// campo (posvenda-fila.jsx:57), e `''` numa coluna date é erro 22007 do Postgres,
// não nulo — a repactuação de data falharia justamente ao ser apagada.
const CAMPOS_DATA_NEGOCIO = new Set(['previsaoEntrega']);

// Campos cujo VAZIO a tela escreve como travessao. "—" e traco de exibicao, nao
// texto do vendedor: gravar o travessao faria o banco guardar um caractere de
// layout como se fosse a proxima acao combinada com o cliente -- e ele voltaria
// na listagem, no CSV e no relatorio parecendo conteudo.
const PLACEHOLDER_TRAVESSAO = new Set(['proximaAcao']);

export function negocioPatchParaBanco(patch = {}) {
  const dbPatch = {};
  for (const [telaKey, dbKey] of Object.entries(CAMPOS_NEGOCIO)) {
    if (patch[telaKey] === undefined) continue;
    const valor = patch[telaKey];
    // `tabelaId` vazio = "sem tabela": '' numa coluna uuid e erro 22P02, nao nulo.
    const vazio = (CAMPOS_DATA_NEGOCIO.has(telaKey) && valor === '')
      || (PLACEHOLDER_TRAVESSAO.has(telaKey) && valor === '—')
      || (telaKey === 'tabelaId' && valor === '');
    dbPatch[dbKey] = vazio ? null : valor;
  }

  // BANT: a tela tem quatro caixas, o banco tem um número (0006:26,
  // `check (bant_score between 0 and 4)`). O número sai dos booleanos; os
  // booleanos NÃO saem do número — score 3 não diz quais três. Por isso
  // `mapearNegocio` devolve `bant: {}` e as marcações não voltam: virar quatro
  // colunas é decisão de modelo, registrada na spec de 14/09, não consequência
  // desta tradução.
  if (patch.bant && typeof patch.bant === 'object') {
    dbPatch.bant_score = ['budget', 'authority', 'need', 'timeline']
      .filter((k) => !!patch.bant[k]).length;
  }
  return dbPatch;
}

// ===== Tarefas (a tela chama "alertas") ==============================
//
// ARMADILHA DE NOME, e ela e cara: o campo rotulado "Responsavel" no
// formulario (modals.jsx:735) e guardado na tela como `autor`, e `scopeAlerts`
// (auth.jsx:516) filtra a agenda por ele. O banco separa os dois:
// `responsavel_id` e quem tem de fazer, `autor_id` e quem criou.
//
//   tela.autor  ->  banco.responsavel_id      (quem tem de fazer)
//   sessao      ->  banco.autor_id            (quem criou)
//
// Trocar os dois poria o criador como responsavel: a tarefa sumiria da agenda
// de quem precisa dela e apareceria na de quem so a marcou. A RLS de insert
// (0022:222) exige `autor_id = auth.uid()`, entao o criador nunca e escolha.

const CANAL_TELA_PARA_BANCO = {
  'whatsapp': 'whatsapp', 'telefone': 'telefone', 'e-mail': 'email', 'email': 'email',
  'google meet': 'google_meet', 'presencial': 'presencial', 'sistema': 'outro',
};
const CANAL_BANCO_PARA_TELA = {
  whatsapp: 'WhatsApp', telefone: 'Telefone', email: 'E-mail',
  google_meet: 'Google Meet', presencial: 'Presencial', outro: 'Sistema',
};
// O enum do banco e sem acento (0002:18); a tela escreve "média".
const PRIORIDADE_BANCO = { alta: 'alta', média: 'media', media: 'media', baixa: 'baixa' };
const PRIORIDADE_TELA = { alta: 'alta', media: 'média', baixa: 'baixa' };

function canalParaBanco(canal) {
  if (!canal) return null;
  return CANAL_TELA_PARA_BANCO[String(canal).trim().toLowerCase()] || 'outro';
}

export function mapearTarefa(linha) {
  // `inicio` e a fonte da verdade; `when` continua sendo devolvido (= inicio)
  // porque dezenas de telas leem `alert.when`. `quando` do banco vira espelho.
  const inicio = linha.inicio ? new Date(linha.inicio) : (linha.quando ? new Date(linha.quando) : new Date());
  const fim = linha.fim ? new Date(linha.fim) : new Date(inicio.getTime() + 3600e3);
  return {
    id: linha.id,
    tipo: linha.titulo,
    descricao: linha.descricao || '',
    leadId: linha.negocio_id || null,
    clienteId: linha.cliente_id || null,
    when: inicio,
    inicio,
    fim,
    canal: CANAL_BANCO_PARA_TELA[linha.canal] || 'Sistema',
    prioridade: PRIORIDADE_TELA[linha.prioridade] || 'média',
    feito: !!linha.feito,
    // Quando foi concluida. Os relatorios contam "reunioes realizadas no mes" por ela.
    feitoEm: linha.feito_em || null,
    autor: linha.responsavel_id || linha.autor_id || null,
    criadoPor: linha.autor_id || null,
    // Envolvidos vem de `tarefa_envolvidos` (0063), com o status de cada um.
    // O status e decidido pelo banco no vinculo (0066) e so o participante o
    // muda depois. `convites` guarda esse status por pessoa para a tela.
    envolvidos: (linha.tarefa_envolvidos || []).map((e) => e.user_id),
    convites: Object.fromEntries((linha.tarefa_envolvidos || []).map((e) => [e.user_id, e.status])),
    subtarefas: (linha.subtarefas || [])
      .slice()
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
      .map((st) => ({ id: st.id, label: st.texto, done: !!st.feito })),
  };
}

// `autorId` e SEMPRE a sessao, nunca o formulario: a RLS de insert recusa
// qualquer outra coisa com 42501.
export function tarefaParaBanco(alerta, autorId) {
  const inicio = alerta.inicio instanceof Date ? alerta.inicio
    : new Date(alerta.inicio || alerta.when || Date.now());
  // Fim: o que a tela mandou, ou um fallback de 1h para nunca violar o
  // check(fim>inicio) nem o not-null. A duracao real por tipo mora no store.
  const fim = alerta.fim instanceof Date ? alerta.fim
    : (alerta.fim ? new Date(alerta.fim) : new Date(inicio.getTime() + 3600e3));
  return {
    titulo: alerta.tipo,
    descricao: alerta.descricao || null,
    negocio_id: alerta.leadId || null,
    cliente_id: alerta.clienteId || null,
    quando: inicio.toISOString(),
    inicio: inicio.toISOString(),
    fim: fim.toISOString(),
    canal: canalParaBanco(alerta.canal),
    prioridade: PRIORIDADE_BANCO[String(alerta.prioridade || 'média').toLowerCase()] || 'media',
    feito: !!alerta.feito,
    autor_id: autorId,
    responsavel_id: alerta.autor || autorId,
  };
}

export function tarefaPatchParaBanco(patch = {}) {
  const db = {};
  if (patch.tipo !== undefined) db.titulo = patch.tipo;
  if (patch.descricao !== undefined) db.descricao = patch.descricao || null;
  if (patch.leadId !== undefined) db.negocio_id = patch.leadId || null;
  if (patch.clienteId !== undefined) db.cliente_id = patch.clienteId || null;
  if (patch.canal !== undefined) db.canal = canalParaBanco(patch.canal);
  if (patch.prioridade !== undefined) db.prioridade = PRIORIDADE_BANCO[String(patch.prioridade).toLowerCase()] || 'media';
  if (patch.autor !== undefined) db.responsavel_id = patch.autor || null;
  // `when` e apelido de inicio na tela: mexer nele move inicio e quando juntos.
  if (patch.when !== undefined && patch.inicio === undefined) {
    const d = patch.when instanceof Date ? patch.when : new Date(patch.when);
    db.quando = d.toISOString();
    db.inicio = d.toISOString();
  }
  if (patch.inicio !== undefined) {
    const d = patch.inicio instanceof Date ? patch.inicio : new Date(patch.inicio);
    db.inicio = d.toISOString();
    db.quando = d.toISOString();
  }
  if (patch.fim !== undefined) {
    const d = patch.fim instanceof Date ? patch.fim : new Date(patch.fim);
    db.fim = d.toISOString();
  }
  if (patch.feito !== undefined) {
    db.feito = !!patch.feito;
    // `feito_em` acompanha o estado. Deixar a data antiga numa tarefa reaberta
    // faria o relatorio contar como concluida uma tarefa que voltou a aberta.
    db.feito_em = patch.feito ? new Date().toISOString() : null;
  }
  return db;
}

// ===== Metas ===========================================================
//
// A tabela `metas` (0010) guarda uma linha por ALVO e MES: escopo
// (empresa/funil/usuario), a metrica e a competencia (1o dia do mes). A tela
// quer um objeto so -- { empresa, porFunil: {codigo}, individuais: {uid: {campo}} }.
//
// META VIGENTE: cada meta vale ate ser redefinida. Para um mes, cada alvo usa a
// linha de competencia mais recente <= aquele mes -- POR ALVO, nao por mes
// inteiro: redefinir so a meta da empresa em setembro nao zera a dos funis, que
// seguem valendo desde agosto. Sem isso, virar o mes zeraria todas as metas e o
// gestor leria "0%" onde havia meta definida.
//
// O dinheiro se chama `faturamento` no banco (empresa, funil e usuario usam o
// mesmo nome) e `valor` na tela individual. As demais metricas batem 1:1.
const METRICA_PARA_CAMPO = { faturamento: 'valor' };
const CAMPO_PARA_METRICA = { valor: 'faturamento' };
const campoDaMetrica = (m) => METRICA_PARA_CAMPO[m] || m;
export const metricaDoCampo = (c) => CAMPO_PARA_METRICA[c] || c;

// '2026-09' -> '2026-09-01'. Competencia e sempre o 1o dia do mes (0010).
export const competenciaDoMes = (monthId) => `${monthId}-01`;

export function metasVigentes(linhas = [], monthId = null, funis = []) {
  const codigoDoFunil = Object.fromEntries((funis || []).map((f) => [f.uuid, f.id]));
  const limite = monthId ? competenciaDoMes(monthId) : null;
  const vigente = {};
  for (const l of linhas || []) {
    const comp = String(l.competencia || '').slice(0, 10);
    // Meta de mes futuro ainda nao vale. Datas 'AAAA-MM-DD' comparam como texto.
    if (!comp || (limite && comp > limite)) continue;
    let chave = null;
    if (l.escopo === 'empresa' && l.metrica === 'faturamento') chave = 'empresa';
    else if (l.escopo === 'funil' && l.metrica === 'faturamento' && codigoDoFunil[l.funil_id]) chave = `funil|${codigoDoFunil[l.funil_id]}`;
    else if (l.escopo === 'usuario' && l.profile_id) chave = `usuario|${l.profile_id}|${campoDaMetrica(l.metrica)}`;
    // escopo `papel` e metricas que a tela nao mostra ficam de fora -- nao ha
    // onde exibi-las, e inventar um lugar seria outra tela mentindo.
    if (!chave) continue;
    if (!vigente[chave] || comp > vigente[chave].comp) vigente[chave] = { comp, valor: Number(l.valor_meta) || 0 };
  }
  // `competencias` diz DE QUANDO cada meta vem, para a tela avisar quando a
  // meta exibida foi herdada de um mes anterior.
  const out = { empresa: 0, porFunil: {}, individuais: {}, competencias: {} };
  for (const [chave, { comp, valor }] of Object.entries(vigente)) {
    out.competencias[chave] = comp.slice(0, 7);
    const [tipo, a, b] = chave.split('|');
    if (tipo === 'empresa') out.empresa = valor;
    else if (tipo === 'funil') out.porFunil[a] = valor;
    else (out.individuais[a] ||= {})[b] = valor;
  }
  return out;
}

// Um negócio está parado quando ficou na etapa mais tempo que o SLA dela.
// Etapa sem SLA (GANHO, PERDIDO, PRODUCAO) nunca acusa: ficar lá é o normal.
// Sem registro de movimento também não acusa — ausência de dado não é atraso.
export function estaParado(lead, slaDias) {
  if (!slaDias || !lead?.ultimoMovimento) return false;
  const dias = (Date.now() - new Date(lead.ultimoMovimento).getTime()) / 86400000;
  return dias > slaDias;
}

// Lê o resultado de UMA consulta inteiro, em páginas (levantamento de 02/10). `lerTudo` (abaixo) faz isso para clientes
// e produtos; este serve a qualquer consulta, com filtro e ordem próprios.
//
// MESMA ARMADILHA, medida de novo com dado real: `max_rows = 1000` corta com HTTP 200 e sem erro. `listarNegocios`,
// `listarPropostas` e `listarTarefas` devolviam 1.000 de 1.120, 1.050 e 1.100 linhas. O teste que prova isto grava mais
// de mil linhas e lê pela mesma função da tela (tests/unit/leituras-passam-de-mil-linhas.test.js).
//
// `consulta()` monta a consulta DO ZERO a cada página (o builder do PostgREST não é reutilizável) e tem de pedir
// `{ count: 'exact' }` e uma ordem ESTÁVEL (com desempate por `id`; sem ele, linhas empatadas se repetem ou somem
// na fronteira entre páginas).
//
// Só FALTA é erro: menos linhas que o total do servidor lança. MAIS linhas (alguém gravou no meio da leitura) é
// normal, e as repetições da fronteira entre páginas são removidas por `id`. Mas uma gravação alheia no TOPO da lista
// durante a leitura também deixa uma linha de fora (ela entrou antes da página que já passamos): por isso a leitura
// inteira é refeita UMA vez antes de lançar. Duas faltas seguidas são falha de verdade.
async function lerEmPaginas(consulta, { oQue, chave = 'id' }) {
  const PAGINA = 1000;
  let falta = '';
  for (let tentativa = 1; tentativa <= 2; tentativa++) {
    const vistos = new Map();
    let total = null;
    for (let inicio = 0; ; inicio += PAGINA) {
      const { data, error, count } = await consulta().range(inicio, inicio + PAGINA - 1);
      const pagina = conferir({ data, error });
      if (total === null) total = count;
      for (const linha of pagina) vistos.set(linha[chave] ?? `${inicio}:${vistos.size}`, linha);
      if (pagina.length < PAGINA) break;
      if (total != null && vistos.size >= total) break;
    }
    if (total == null || vistos.size >= total) return [...vistos.values()];
    falta = `${vistos.size} de ${total}`;
  }
  throw new Error(`Carga incompleta (${oQue}): ${falta}.`);
}

// Lê uma tabela INTEIRA, em páginas, e confere o total com o servidor.
//
// ARMADILHA MEDIDA, e ela é muda. `supabase/config.toml:18` deste projeto traz
// `max_rows = 1000`, e a documentação do PostgREST descreve o efeito: "A hard
// limit to the number of rows PostgREST will fetch from a view, table, or
// function." — https://docs.postgrest.org/en/v12/references/configuration.html
//
// Um `.select('*')` numa tabela maior que isso devolve 1.000 linhas com HTTP 200
// e sem erro nenhum. São 2.201 clientes e 12.723 produtos: sem paginar, o
// vendedor procuraria um cadastro que existe, não acharia, e criaria duplicata
// no ERP. Estas são as duas primeiras tabelas do projeto a passar de mil linhas.
//
// O `count` exato não é zelo: é o que torna o corte IMPOSSÍVEL em silêncio. Sem
// ele, um laço que pare cedo (RLS recusando no meio, página vazia por acaso)
// devolveria uma lista parcial com a mesma cara de uma completa. `exact` e não
// `planned` porque planned depende de estatística atualizada, e aqui o número
// decide se o aplicativo avisa ou não que faltou gente.
async function lerTudo(cliente, { tabela, colunas, ordem, oQue }) {
  const PAGINA = 1000;
  const linhas = [];
  let total = null;
  for (let inicio = 0; ; inicio += PAGINA) {
    const { data, error, count } = await cliente
      .from(tabela)
      .select(colunas, { count: 'exact' })
      .is('deleted_at', null)
      .order(ordem, { ascending: true })
      .range(inicio, inicio + PAGINA - 1);
    const pagina = conferir({ data, error });
    if (total === null) total = count;
    linhas.push(...pagina);
    // Duas saídas: página incompleta (caso comum) e total atingido. A segunda
    // evita uma ida extra quando o total é múltiplo exato de 1.000 — sem ela,
    // 2.000 linhas custariam três requisições, a última só para ouvir "acabou".
    if (pagina.length < PAGINA) break;
    if (total != null && linhas.length >= total) break;
  }
  if (total != null && linhas.length !== total) {
    // Exceção, não console.error: quem chama já mostra toast, e uma lista pela
    // metade que se parece com a inteira é pior que lista nenhuma.
    throw new Error(`Carga incompleta de ${oQue}: ${linhas.length} de ${total}.`);
  }
  return linhas;
}

export function criarApi(cliente) {
  // Nichos (7 linhas): a tela fala em CODIGO, a coluna `clientes.nicho_id` guarda uuid.
  // A conversao mora aqui, na camada de dados, para a leitura e a escrita usarem o
  // mesmo mapa (spec 2026-10-30-clientes-filtro-de-nicho-e-de-alertas, N-D1).
  //
  // Guarda-se a PROMESSA, e nao o resultado: duas chamadas ao mesmo tempo (a carga e
  // um salvar) dividem uma ida so ao banco. Falha nao fica guardada -- a promessa e
  // descartada ao rejeitar, e a proxima chamada tenta de novo. Cachear a falha
  // deixaria o nicho quebrado ate o F5.
  let nichosPromessa = null;
  function carregarNichos() {
    if (!nichosPromessa) {
      const atual = (async () => {
        const linhas = conferir(await cliente.from('nichos').select('id, codigo'));
        // Lista vazia com HTTP 200 e o que a RLS devolve quando nega (CLAUDE.md, §5).
        // O seed tem 7 nichos, entao vazio nunca e "nao ha nichos": e falha, e visivel.
        if (!linhas.length) throw new Error('A tabela de nichos veio vazia.');
        const porId = new Map();
        const porCodigo = new Map();
        for (const l of linhas) { porId.set(l.id, l.codigo); porCodigo.set(l.codigo, l.id); }
        return { porId, porCodigo };
      })();
      nichosPromessa = atual;
      atual.catch(() => { if (nichosPromessa === atual) nichosPromessa = null; });
    }
    return nichosPromessa;
  }

  // Poe `nicho_codigo` na linha. MELHOR ESFORCO: roda DEPOIS de a gravacao dar certo,
  // e lancar aqui faria a tela dizer "nao foi possivel criar" para um cliente que ja
  // existe -- o usuario tentaria de novo e duplicaria. Sem o mapa, fica `null`; quem
  // avisa da falha e a carga (store.jsx), que chama `carregarNichos` direto.
  async function comCodigoDoNicho(linha) {
    if (!linha) return linha;
    let codigo = null;
    if (linha.nicho_id) {
      try { codigo = (await carregarNichos()).porId.get(linha.nicho_id) ?? null; } catch (e) {
        console.error('Não foi possível carregar os nichos:', e.message);
      }
    }
    return { ...linha, nicho_codigo: codigo };
  }

  // ANTES de gravar: troca a chave interna do patch (codigo) pelo uuid de `nicho_id`.
  // Rejeita sem chamar o banco quando nao ha como gravar certo -- descartar o campo
  // em silencio faria a tela mostrar "salvo" e o banco guardar o nicho antigo.
  // Patch sem a chave nao toca `nicho_id`: editar so o nome nao pode apagar o nicho.
  async function resolverNichoDoPatch(patch) {
    if (!patch || !(CHAVE_NICHO_PARA_RESOLVER in patch)) return patch;
    const { [CHAVE_NICHO_PARA_RESOLVER]: codigo, ...resto } = patch;
    if (codigo == null) return { ...resto, nicho_id: null };
    let mapa;
    try { mapa = await carregarNichos(); } catch (e) {
      const falha = new Error('Não foi possível carregar os nichos');
      falha.tipo = 'nicho';
      falha.causa = e;
      throw falha;
    }
    const uuid = mapa.porCodigo.get(codigo);
    if (!uuid) {
      const falha = new Error('Nicho inválido');
      falha.tipo = 'nicho';
      throw falha;
    }
    return { ...resto, nicho_id: uuid };
  }

  // Grava UMA meta. Upsert feito a mao, e ha dois motivos:
  //
  // 1. A chave unica da tabela (`uq_meta_alvo`, 0010) e um indice de EXPRESSAO
  //    -- COALESCE(funil_id, ...), COALESCE(profile_id, ...). O `on_conflict`
  //    do PostgREST so aceita lista de colunas, entao `upsert` nao consegue
  //    mira-la: procura-se o alvo e atualiza, ou insere.
  // 2. NULL casa com `.is(col, null)`, NUNCA com `.eq(col, null)`. `eq` vira
  //    `col = null`, que em SQL nao e verdadeiro para nenhuma linha -- a busca
  //    voltaria vazia e cada gravacao tentaria inserir de novo.
  //
  // Corrida: dois gestores gravando o mesmo alvo ao mesmo tempo. O segundo
  // insert bate no indice unico (23505); a linha ja existe, entao uma segunda
  // passada a encontra e atualiza. Uma tentativa extra so -- se ainda falhar,
  // o erro sobe em vez de girar em laco.
  async function gravarMetaNoBanco(alvo, jaTentou = false) {
    const { escopo, funilId = null, profileId = null, competencia, metrica, valor } = alvo;
    let busca = cliente.from('metas').select('id')
      .eq('escopo', escopo).eq('competencia', competencia).eq('metrica', metrica)
      .is('papel', null);
    busca = funilId ? busca.eq('funil_id', funilId) : busca.is('funil_id', null);
    busca = profileId ? busca.eq('profile_id', profileId) : busca.is('profile_id', null);
    const existente = conferir(await busca.maybeSingle());
    // `existente?.id`, e NAO `if (existente)`: sem linha, `conferir` devolve
    // `[]` (o padrao dele para listagens), e `[]` e verdadeiro em JS -- a
    // gravacao iria para o update com `id` indefinido e o banco recusaria com
    // "invalid input syntax for type uuid". Medido em 22/09, teste contra o banco.
    if (existente?.id) {
      const atualizadas = conferir(await cliente.from('metas')
        .update({ valor_meta: valor }).eq('id', existente.id).select());
      if (atualizadas.length) return atualizadas[0];
      // UPDATE que nao tocou linha nenhuma, sobre uma linha que o SELECT acabou
      // de ver: e a RLS de escrita (metas_mod, so gestor) recusando. O PostgREST
      // NAO devolve erro nesse caso -- so zero linhas. Com `.single()` isso
      // virava PGRST116 ("no rows"), e a tela mandaria "tentar de novo" algo que
      // nunca vai passar. Medido em 22/09 com um closer real. Traduz para 42501,
      // o codigo de permissao. (Linha apagada entre o SELECT e o UPDATE daria o
      // mesmo sinal, mas metas nao se apagam pela aplicacao.)
      const falha = new Error('Sem permissão para alterar esta meta.');
      falha.code = '42501';
      throw falha;
    }
    const r = await cliente.from('metas')
      .insert({ escopo, funil_id: funilId, profile_id: profileId, competencia, metrica, valor_meta: valor })
      .select().single();
    if (r.error?.code === '23505' && !jaTentou) return gravarMetaNoBanco(alvo, true);
    return conferir(r);
  }

  // Congela a descricao TECNICA do produto nos itens, no instante da proposta.
  //
  // POR QUE AQUI E NAO NA CARGA DO CATALOGO: `listarProdutos` escolhe colunas a
  // dedo justamente porque sao 12.723 linhas -- o comentario de la avisa que a
  // diferenca e de megabytes por carga. Descricao tecnica e texto longo; com o
  // catalogo preenchido seriam alguns megabytes trafegados toda vez que alguem
  // abre o sistema, para usar meia duzia deles.
  //
  // Aqui a consulta pede so os produtos DESTA proposta. Itens sem
  // `id_produto_nomus` (digitados a mao) passam sem enriquecimento, que e o
  // correto: nao ha produto de onde copiar.
  async function congelarDescricaoTecnica(itens) {
    const ids = [...new Set(itens.map((it) => it.id_produto_nomus).filter(Boolean))];
    if (!ids.length) return itens;
    let porId = new Map();
    try {
      const linhas = conferir(await cliente
        .from('produtos')
        .select('id_produto_nomus, descricao_estendida, dimensoes')
        .in('id_produto_nomus', ids));
      porId = new Map((linhas || []).map((p) => [p.id_produto_nomus, p]));
    } catch (e) {
      // Falhar aqui NAO pode impedir a proposta de ser salva: a descricao e
      // enfeite do documento, o preco e o item sao o negocio. Mas tem de
      // aparecer -- devolver em silencio faria a descricao sumir sem motivo
      // visivel (§5).
      console.error('Nao foi possivel copiar a descricao tecnica dos produtos:', e.message);
      return itens;
    }
    return itens.map((it) => {
      const p = porId.get(it.id_produto_nomus);
      if (!p) return it;
      return {
        ...it,
        // O que veio da tela vence: se o vendedor editou a descricao naquela
        // proposta, e a dele que vale.
        descricao_estendida: it.descricao_estendida || p.descricao_estendida || null,
        dimensoes: it.dimensoes || p.dimensoes || null,
      };
    });
  }


  return {
    async listarFunis() {
      return conferir(await cliente
        .from('funis')
        .select('id, codigo, nome, nome_curto, subtitulo, descricao, ciclo, icon, accent, ordem')
        .order('ordem', { ascending: true }));
    },

    async listarEtapas() {
      return conferir(await cliente
        .from('etapas')
        .select('id, funil_id, codigo, nome, nome_curto, ordem, sla_dias, is_ganho, is_perdido')
        .order('ordem', { ascending: true }));
    },

    async listarNegocios() {
      return lerEmPaginas(() => cliente
        .from('negocios')
        // Os elos gravados da cadeia vem junto (reteste do Kaio, 25/09: a aba
        // Cadeia perdia tudo no F5 -- `cadeia` saia sempre vazia daqui).
        .select('*, negocio_cadeia(seq, tipo, ator_tipo, ator_user_id, ator_agente_id, ator_nome, papel, ocorreu_em)', { count: 'exact' })
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true }), { oQue: 'os negócios' });
    },

    // ===== BANT do negocio (Budget, Authority, Need, Timeline) =====
    //
    // A gaveta tinha quatro campos de texto "editaveis" e `updateBant` so mexia na memoria; o modal de edicao
    // mandava so o NUMERO (`bant_score`). O texto sumia no F5 (levantamento de 02/10). A tabela `negocio_bant`
    // existe desde a 0013: LEITURA segue a visibilidade do negocio (0079) e ESCRITA e do dono ou do gestor (0015).
    // Nada de banco muda; so a tela passa a usa-la. Uma linha por negocio (`negocio_id` e a chave).
    async listarBant() {
      return lerEmPaginas(() => cliente
        .from('negocio_bant')
        .select('negocio_id, budget, authority, need, timeline', { count: 'exact' })
        .order('negocio_id', { ascending: true }), { oQue: 'a qualificação BANT', chave: 'negocio_id' });
    },

    // Grava os QUATRO campos de uma vez (a linha inteira). Vazio ou so espaco vira nulo: o painel de saude do funil
    // trata "preenchido" como texto nao vazio. `autorId` fica em `atualizado_por`.
    async salvarBant(negocioId, bant, autorId) {
      const texto = (v) => { const t = typeof v === 'string' ? v.trim() : v; return t ? t : null; };
      return conferir(await cliente
        .from('negocio_bant')
        .upsert({
          negocio_id: negocioId,
          budget: texto(bant?.budget), authority: texto(bant?.authority), need: texto(bant?.need), timeline: texto(bant?.timeline),
          atualizado_por: autorId ?? null,
        }, { onConflict: 'negocio_id' })
        .select('negocio_id, budget, authority, need, timeline')
        .single());
    },

    // Usa conferir() (não um `if (error) throw`) pelo mesmo motivo do resto do
    // arquivo: preservar code/details/hint. Numa escrita isso pesa mais ainda —
    // 42501 aqui costuma ser owner_id != auth.uid(), e sem o code essa causa
    // fica indistinguível de qualquer outra falha de gravação.
    //
    // `ultimo_movimento_em` é gravado aqui, sempre, por fora do que o chamador
    // manda em `dados` — a coluna (db/migrations/0006:46) é timestamptz sem
    // default e sem gatilho, e é o único dado que sustenta `estaParado`. Se
    // depender de o chamador lembrar de incluí-la, o negócio nasce com o
    // campo NULL e o alerta de lead parado nunca dispara para ele (achado 3
    // da revisão final) — o mesmo raciocínio que já vale para moverNegocio
    // logo abaixo.
    async criarNegocio(dados) {
      return conferir(await cliente
        .from('negocios')
        .insert({ ...dados, ultimo_movimento_em: new Date().toISOString() })
        .select().single());
    },

    async moverNegocio(id, etapaId) {
      // `ultimo_movimento_em` alimenta o alerta de lead parado da Task 5.
      // Gravado aqui, e não por gatilho, para o motivo ficar legível no código
      // que provoca o movimento.
      return conferir(await cliente
        .from('negocios')
        .update({ etapa_id: etapaId, ultimo_movimento_em: new Date().toISOString(), dias_no_estagio: 0 })
        .eq('id', id).select().single());
    },

    // Um elo da cadeia de atribuição (quem passou para quem, e quando).
    //
    // `negocio_cadeia` existe desde a 0006 (linha 90) e NUNCA recebeu uma linha:
    // `handoffToCloser` só mexia na memória. É ela que sustenta a promessa do
    // toast — "origem preservada no funil de quem iniciou o lead" —, então sem
    // gravar, a promessa durava até o F5.
    //
    // `seq` vem de quem chama porque a cadeia é ordenada e o próximo número
    // depende dos elos que já existem. O `unique (negocio_id, seq)` da 0006 é a
    // rede: dois repasses simultâneos colidem em vez de embaralhar a ordem.
    async registrarCadeia(negocioId, elo) {
      return conferir(await cliente
        .from('negocio_cadeia')
        .insert({
          negocio_id: negocioId,
          seq: elo.seq,
          tipo: elo.tipo,
          ator_tipo: elo.atorTipo || 'humano',
          ator_user_id: elo.atorId || null,
          ator_nome: elo.atorNome || null,
          papel: elo.papel || null,
        })
        .select().single());
    },

    // PATCH de um negócio. Substitui `registrarPedidoNomus`, que fazia só o par
    // pedido/previsão e nunca chegou a ser chamado por ninguém — dois caminhos
    // para a mesma gravação, um deles morto, é como bug nasce.
    //
    // `.select().single()` de propósito: sob a policy `negocios_upd` (0022:176)
    // um UPDATE em negócio de outro dono não dá erro, dá ZERO linhas — e
    // `.single()` transforma isso em erro, que é o que o store precisa para não
    // dizer "salvo" sobre o que o banco recusou. Já tentar MUDAR o dono cai no
    // `with check` e vem como 42501; conferir() preserva o code nos dois casos.
    async atualizarNegocio(id, dbPatch) {
      return conferir(await cliente
        .from('negocios')
        .update(dbPatch)
        .eq('id', id).select().single());
    },

    // Envia a proposta ao Nomus. Toda a decisão mora na Edge Function
    // `enviar-pedido-nomus`: só ela tem a credencial do ERP (§4 — credencial não
    // entra no bundle) e só ela vê a sequência de CP ao vivo, que é onde a
    // colisão de número acontece.
    //
    // O número do pedido NÃO vai daqui. O ERP atribui o CP na proposta e o
    // pedido herda; escolher do lado do navegador colidiria com uma proposta
    // aberta no Nomus há segundos.
    async enviarPedidoAoNomus(propostaId, payload) {
      // As duas falhas abaixo são MUITO diferentes para quem está na tela: uma
      // se resolve entrando de novo, a outra é tentar mais tarde. Até 14/09 as
      // duas viravam o mesmo "Não foi possível falar com o Nomus" no store, que
      // jogava fora justamente a parte acionável — o vendedor ficava repetindo
      // um clique que nunca ia funcionar. Por isso cada uma leva `code`.
      const { data: sessao } = await cliente.auth.getSession();
      const token = sessao?.session?.access_token;
      if (!token) {
        throw Object.assign(new Error('Sua sessão expirou. Entre de novo para enviar o pedido.'), { code: 'SEM_SESSAO' });
      }
      let r;
      try {
        r = await fetch(`${cliente.supabaseUrl}/functions/v1/enviar-pedido-nomus`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ propostaId, payload }),
        });
      } catch (e) {
        // `fetch` só rejeita por rede/CORS/DNS — status de erro do servidor vem
        // como resposta normal. Guardar a causa original importa: é ela que
        // distingue "sem internet" de "a função não subiu".
        throw Object.assign(
          new Error('Não foi possível falar com o servidor. Verifique a conexão e tente de novo.'),
          { code: 'REDE', causa: e?.message },
        );
      }
      const corpo = await r.json().catch(() => ({}));
      // O corpo viaja junto do status: a tela precisa distinguir "em voo" (não
      // oferecer reenvio) de recusa comum (oferecer nova tentativa).
      return { ok: r.ok, status: r.status, ...corpo };
    },

    // Invalida o link publico da proposta.
    //
    // Par da expiracao de 180 dias (decisao do Kaio, 16/09): o prazo cobre o
    // esquecimento, a revogacao cobre o acidente -- proposta mandada para o
    // endereco errado, ou cliente que nao deve mais ver aquele preco.
    //
    // Apaga o TOKEN e nao a proposta. Quem tiver o endereco antigo passa a ver
    // "Este link nao e mais valido"; um envio novo gera token novo, e a proposta
    // segue intacta com o historico de aberturas.
    async revogarLinkProposta(propostaId) {
      return conferir(await cliente
        .from('propostas')
        .update({ token_publico: null })
        .eq('id', propostaId)
        .select('id, token_publico').single());
    },

    // Envia a proposta ao CLIENTE por e-mail. Mesma forma da função acima e pela
    // mesma razão: a chave do Resend não entra no pacote do navegador (§4).
    //
    // O que existia antes aqui era pior que nada — uma linha de log em
    // store.jsx que registrava "Resend · status 200" sem nenhum e-mail sair.
    // Quem olhasse a tela de logs concluiria que o cliente recebeu.
    async enviarPropostaPorEmail(propostaId) {
      const { data: sessao } = await cliente.auth.getSession();
      const token = sessao?.session?.access_token;
      if (!token) {
        throw Object.assign(new Error('Sua sessão expirou. Entre de novo para enviar a proposta.'), { code: 'SEM_SESSAO' });
      }
      let r;
      try {
        r = await fetch(`${cliente.supabaseUrl}/functions/v1/enviar-proposta-email`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ propostaId }),
        });
      } catch (e) {
        throw Object.assign(
          new Error('Não foi possível falar com o servidor. Verifique a conexão e tente de novo.'),
          { code: 'REDE', causa: e?.message },
        );
      }
      const corpo = await r.json().catch(() => ({}));
      return { ok: r.ok, status: r.status, ...corpo };
    },

    // Busca um pedido pela referência que o CLIENTE tem na mão quando liga:
    // o CP, o número da nota ou a chave de 44 dígitos do DANFE.
    //
    // Consulta o banco em vez de filtrar em memória porque são 4.190 pedidos e
    // 4.311 notas, crescendo — carregar tudo no navegador para achar um é o tipo
    // de coisa que funciona na demonstração e trava na operação.
    //
    // O CP é comparado normalizado (sem espaço, caixa alta) contra o índice
    // `ix_pedidos_nomus_codigo`, que guarda a mesma expressão. Sem isso "cp
    // 10240" e "CP10240" não achariam a mesma linha — e um espaço a mais some
    // com o pedido, que é o defeito que esta busca veio corrigir.
    async buscarPedidoPorReferencia(termo) {
      const t = String(termo || "").trim();
      if (t.length < 3) return [];
      const cp = t.toUpperCase().replace(/\s/g, "");
      const digitos = t.replace(/\D/g, "");

      // Duas consultas em vez de um `or` com join: o PostgREST não filtra a
      // tabela-pai por coluna da filha sem embutir a filha, e embutir aqui
      // traria nota junto de todo pedido só para descartar.
      const porCodigo = conferir(await cliente
        .from('pedidos_nomus')
        .select('id, codigo_pedido, data_emissao, valor_total, id_pessoa_nomus')
        .ilike('codigo_pedido', '%' + t.replace(/\s/g, '%') + '%')
        .limit(5));

      // Chave tem 44 dígitos; número da nota é curto. Buscar número com menos de
      // 3 dígitos traria meio catálogo, então o piso acima já barrou.
      const porNota = digitos.length >= 3
        ? conferir(await cliente
            .from('notas_fiscais')
            .select('id, numero, serie, chave, pedido_notas(pedidos_nomus(id, codigo_pedido, data_emissao, valor_total, id_pessoa_nomus))')
            .or(`chave.eq.${digitos},numero.eq.${digitos}`)
            .limit(5))
        : Promise.resolve([]);

      const [pedidos, notas] = await Promise.all([porCodigo, porNota]);
      const achados = new Map();
      for (const p of (pedidos || [])) achados.set(p.id, { ...p, via: 'CP' });
      for (const n of (notas || [])) {
        for (const lig of (n.pedido_notas || [])) {
          const p = lig.pedidos_nomus;
          // Uma nota consolidada cobre mais de um pedido (medido: 46 delas).
          // Todos entram: mostrar só o primeiro esconderia os demais de quem
          // está com o DANFE na mão.
          if (p && !achados.has(p.id)) achados.set(p.id, { ...p, via: 'NF ' + n.numero });
        }
      }
      return [...achados.values()];
    },

    // Viaja dentro do objeto da api (em vez de ser importado direto por
    // store.jsx) porque store.jsx não pode importar api.js/supabase.js — ver
    // nota no topo do arquivo.
    mapear: mapearNegocio,

    // Mesma razão de `mapear` acima: store.jsx não pode importar este arquivo,
    // então os mapeadores de proposta viajam dentro do objeto da api.
    mapearProposta,
    propostaParaBanco,
    propostaPatchParaBanco,
    mapearCliente,
    mapearProduto,
    mapearNotificacao,
    mapearTabelaPreco,
    clientePatchParaBanco,
    enderecoParaBanco,
    negocioPatchParaBanco,
    mapearTarefa, tarefaParaBanco, tarefaPatchParaBanco,
    metasVigentes, competenciaDoMes, metricaDoCampo,

    // Traz cargo, escopos e permissões numa consulta só. O PostgREST aninha
    // tabelas relacionadas pela chave estrangeira -- evita três idas ao banco
    // para montar uma matriz que a tela precisa inteira de qualquer forma.
    // A carteira inteira, em páginas — e com o total conferido.
    //
    // ARMADILHA MEDIDA, e ela é muda. `supabase/config.toml:18` deste projeto
    // traz `max_rows = 1000`, e a documentação do PostgREST descreve o que isso
    // faz: "A hard limit to the number of rows PostgREST will fetch from a view,
    // table, or function."
    // https://docs.postgrest.org/en/v12/references/configuration.html
    //
    // São 2.201 clientes. Um `.select('*')` devolveria 1.000 deles com HTTP 200
    // e sem erro nenhum — os outros 1.201 simplesmente não existiriam para o
    // aplicativo, e o vendedor que procurasse um deles concluiria que não está
    // cadastrado e criaria duplicata no ERP. `clientes` é a primeira tabela do
    // projeto a passar de mil linhas; por isso isto não apareceu antes.
    //
    // O `count` exato não é zelo: é o que torna o corte IMPOSSÍVEL em silêncio.
    // Sem ele, um laço que pare cedo (RLS recusando no meio, página vazia por
    // acaso) devolveria uma carteira parcial com a mesma cara de uma completa.
    // `exact` e não `planned` porque planned depende de estatística atualizada —
    // aqui o número decide se o aplicativo avisa ou não que faltou gente, e
    // 2.201 linhas tornam o custo irrelevante.
    async listarClientes() {
      const linhas = await lerTudo(cliente, {
        // `enderecos(*)` aninhado: o endereco tem tabela propria (0005) e ate
        // 16/09 a tela recebia sempre vazio. Uma consulta so, pela chave
        // estrangeira, em vez de 2.201 idas ao banco.
        tabela: 'clientes', colunas: '*, enderecos(*)', ordem: 'nome', oQue: 'clientes',
      });
      // Falha nos nichos NAO derruba a carteira: os clientes carregam sem nicho e a
      // store avisa com toast (ela chama `carregarNichos` antes). Aqui so o registro.
      let porId = null;
      try { porId = (await carregarNichos()).porId; } catch (e) {
        console.error('Não foi possível carregar os nichos:', e.message);
      }
      return linhas.map((l) => ({ ...l, nicho_codigo: porId ? (porId.get(l.nicho_id) ?? null) : null }));
    },

    // A store chama isto antes de `listarClientes` para poder AVISAR da falha (a
    // carga da carteira engole o erro de nichos para nao bloquear a tela). Comeca
    // por "carregar" para o rastro de escritas (rastro-de-escritas.js) a tratar como leitura.
    carregarNichos,

    // Cria o cliente no Nomus pela Edge Function. A credencial do ERP nao pode
    // entrar no bundle (paragrafo 4 do CLAUDE.md) -- era justamente por montar
    // tudo no navegador que o mock antigo podia inventar o identificador.
    async criarClienteNoNomus(clienteId, payload) {
      const { data, error } = await cliente.functions.invoke('criar-cliente-nomus', {
        body: { clienteId, payload },
      });
      if (error) {
        // A funcao responde com corpo em portugues nos erros de negocio (422,
        // 504). Sem le-lo, o vendedor receberia "Edge Function returned a
        // non-2xx status code", que nao diz o que fazer.
        const corpo = await error.context?.json?.().catch(() => null);
        throw new Error(corpo?.erro || error.message);
      }
      return data;
    },

    // Preco, bloqueio e saldo AO VIVO (Edge Function consultar-produtos-nomus,
    // spec de 23/09). Devolve um resultado POR produto; a regra de envio mora em
    // supabase/functions/consultar-produtos-nomus/logica.ts.
    async consultarProdutosNomus(idsProduto) {
      const { data, error } = await cliente.functions.invoke('consultar-produtos-nomus', { body: { idsProduto } });
      if (error) {
        const corpo = await error.context?.json?.().catch(() => null);
        throw new Error(corpo?.erro || error.message);
      }
      return data.resultados;
    },

    // PERDA E REABERTURA do negocio.
    //
    // Ate 21/09 nem `markLeadLost` nem `reopenLead` gravavam: marcar como
    // perdido e reabrir eram decisao comercial que sumia no F5. O detector de
    // mocks nao pegou o `markLeadLost` porque ele usa `toastWithUndo` em vez do
    // `toast({kind:'ok'})` que o detector procura -- entao ele nao estava nem na
    // lista dos 19.
    //
    // O MOTIVO E RESOLVIDO AQUI, e nao no store, para nao obrigar o CRM a
    // carregar mais uma tabela na subida so por causa de um lookup raro.
    //
    // `motivo_perda_id` e uuid com chave estrangeira; a tela passa o LABEL. Se o
    // label nao casar, grava a perda MESMO ASSIM com motivo nulo: perder o
    // registro inteiro da perda por causa do motivo seria pior que perder o
    // motivo.
    async marcarNegocioPerdido(negocioId, motivoLabel, obs) {
      let motivoId = null;
      if (motivoLabel) {
        const { data } = await cliente.from('motivos_perda')
          .select('id').ilike('label', String(motivoLabel).trim()).limit(1).maybeSingle();
        motivoId = data?.id ?? null;
        if (!motivoId) console.warn('Motivo de perda sem correspondencia no banco:', motivoLabel);
      }
      return conferir(await cliente.from('negocios')
        .update({ perdido: true, motivo_perda_id: motivoId, obs_perda: obs || null })
        .eq('id', negocioId).select('*').single());
    },

    // EXCLUI o lead (DELETE fisico; migration 0090: so nas duas primeiras etapas e com
    // `pipeline.delete_lead`). `.select('id')` para saber se apagou: a RLS devolve zero linhas SEM erro
    // quando o lead nao e alcancavel, e a tela diria "excluido" para uma exclusao que nao aconteceu.
    async excluirNegocio(negocioId) {
      const linhas = conferir(await cliente.from('negocios').delete().eq('id', negocioId).select('id'));
      if (!linhas?.length) throw Object.assign(new Error('Sem permissão para excluir este negócio.'), { code: '42501' });
      return linhas[0];
    },

    async reabrirNegocio(negocioId) {
      // Limpa motivo e observacao junto: um negocio ativo carregando "motivo da
      // perda" aparece como perdido em qualquer relatorio que olhe a coluna.
      return conferir(await cliente.from('negocios')
        .update({ perdido: false, motivo_perda_id: null, obs_perda: null })
        .eq('id', negocioId).select('*').single());
    },

    // ===== Timeline do negocio =====
    //
    // A tabela `timeline_eventos` existe desde a 0008 e o FRONT nunca escreveu
    // nela: so as RPC dos agentes de IA gravavam. O que o vendedor fazia --
    // mover etapa, anotar, repassar -- vivia num useState e sumia no F5.
    //
    // Pior que isso, a tela exibia eventos FABRICADOS por `buildTimeline`, com
    // autor fixo no codigo e data calculada. Historico inventado e mais grave
    // que historico perdido: ninguem tem motivo para desconfiar dele.
    //
    // Em LOTES de 100 negocios e PAGINADA (levantamento de 02/10). Antes era uma consulta so, com todos os ids na
    // URL e `limit(2000)`: a URL estoura por volta de 220 negocios ("URI too long"), e `max_rows = 1000` reduz
    // qualquer limite maior a 1.000 eventos, calado. Com algumas semanas de uso real o historico dos negocios mais
    // antigos sumiria da tela sem aviso. 100 ids deixam a URL perto de 4 KB. Quando o volume de eventos pesar
    // (dezenas de milhares), o desenho certo e carregar por negocio ao abrir a gaveta.
    async listarTimeline(negocioIds) {
      const ids = [...new Set((negocioIds || []).filter(Boolean))];
      if (!ids.length) return [];
      const POR_LOTE = 100;
      const eventos = [];
      for (let i = 0; i < ids.length; i += POR_LOTE) {
        const lote = ids.slice(i, i + POR_LOTE);
        eventos.push(...await lerEmPaginas(() => cliente
          .from('timeline_eventos')
          .select('*', { count: 'exact' })
          .in('negocio_id', lote)
          .order('ocorreu_em', { ascending: false })
          .order('id', { ascending: true }), { oQue: 'o histórico dos negócios' }));
      }
      // Cada lote veio ordenado; a tela espera o conjunto todo do mais novo para o mais antigo.
      return eventos.sort((x, y) => (x.ocorreu_em < y.ocorreu_em ? 1 : x.ocorreu_em > y.ocorreu_em ? -1 : 0));
    },

    async criarEventoTimeline(linha) {
      return conferir(await cliente
        .from('timeline_eventos')
        .insert(linha)
        .select('*').single());
    },

    // CRIA o cliente. Ate 18/09 `createCliente` era `setClientes(...)` e ponto:
    // o vendedor cadastrava, a tela confirmava, e no F5 o cliente nao existia.
    // Era o pior dos 19 casos levantados em 16/09, porque cadastro e a porta de
    // entrada -- sem cliente gravado nao ha proposta, nem pedido, nem nota.
    //
    // O ID VEM DO BANCO. A versao antiga inventava `"cli_" + timestamp`, que
    // nunca corresponderia ao uuid da coluna; qualquer escrita posterior com
    // esse id erraria a linha. Por isso a funcao e assincrona: so depois do
    // insert existe id de verdade.
    //
    // O endereco so pode ser gravado DEPOIS, porque `enderecos.cliente_id`
    // aponta para uma linha que ainda nao existia.
    async criarCliente(patch, enderecoDaTela = null) {
      const paraGravar = await resolverNichoDoPatch(patch);
      const linha = conferir(await cliente
        .from('clientes')
        .insert(paraGravar)
        .select('*, enderecos(*)').single());

      if (enderecoDaTela && Object.values(enderecoDaTela).some(Boolean)) {
        conferir(await cliente.from('enderecos')
          .insert(enderecoParaBanco(linha.id, enderecoDaTela)));
        return comCodigoDoNicho(conferir(await cliente.from('clientes')
          .select('*, enderecos(*)').eq('id', linha.id).single()));
      }
      return comCodigoDoNicho(linha);
    },

    // Grava o cadastro do cliente. Ate 16/09 nao existia -- `updateCliente` era
    // `setClientes(...)` e o formulario descartava tudo no reload.
    //
    // O ENDERECO E OUTRA TABELA, entao sao duas escritas. A do cliente vem
    // primeiro: se a segunda falhar, o cadastro ficou salvo e so o endereco
    // faltou, que e melhor que o contrario.
    async atualizarCliente(id, patch, endereco = null) {
      const paraGravar = await resolverNichoDoPatch(patch);
      const linha = conferir(await cliente
        .from('clientes')
        .update(paraGravar)
        .eq('id', id)
        .select('*, enderecos(*)').single());

      if (endereco) {
        // Um endereco principal por cliente: apaga e poe de novo em vez de
        // tentar adivinhar qual linha atualizar. Sao poucas linhas por cliente e
        // a alternativa e uma chave composta que a 0005 nao criou.
        conferir(await cliente.from('enderecos').delete()
          .eq('cliente_id', id).eq('tipo', 'faturamento'));
        conferir(await cliente.from('enderecos').insert(endereco));
        return comCodigoDoNicho(conferir(await cliente.from('clientes')
          .select('*, enderecos(*)').eq('id', id).single()));
      }
      return comCodigoDoNicho(linha);
    },

    // 12.723 produtos — doze vezes o teto. Mesma paginação da carteira, e por
    // isso mesma função: escrever o laço de novo seria a chance de a segunda
    // cópia esquecer a conferência do total.
    //
    // COLUNAS ESCOLHIDAS, não `*`: a tabela tem 24 colunas e a tela usa sete.
    // Com 12.723 linhas a diferença é de megabytes trafegados a cada carga.
    // `grupos(nome)` vem aninhado porque o filtro do catálogo agrupa por nome
    // (proposta.jsx:87) e a coluna guarda uuid.
    async listarProdutos() {
      return lerTudo(cliente, {
        tabela: 'produtos',
        colunas: 'id, codigo, sku, nome, descricao, id_produto_nomus, unidade_id, preco_lista, categoria, status, descricao_estendida, dimensoes, peso_kg, ncm, origem_produto, grupos(nome)',
        ordem: 'nome', oQue: 'produtos',
      });
    },

    // Lookups do Nomus: a tela precisa deles para DUAS coisas -- montar o select
    // e traduzir a escolha em uuid na hora de gravar. Até a 0052 a lista vinha de
    // constante escrita à mão (src/data.jsx), e nove das dez condições estavam
    // erradas; agora vem de onde o ERP mandou.
    //
    // `idNomus` já sai traduzido para o nome que a tela usa, senão cada chamador
    // repetiria `id_condicao_nomus` e o nome da coluna vazaria para os componentes.
    async listarCondicoesPagamento() {
      const linhas = conferir(await cliente
        .from('condicoes_pagamento')
        .select('id, id_condicao_nomus, nome, ativo, ordem')
        .order('ordem', { ascending: true }));
      return linhas.map((l) => ({ id: l.id, idNomus: l.id_condicao_nomus, nome: l.nome, ativo: l.ativo }));
    },

    async listarTiposVenda() {
      const linhas = conferir(await cliente
        .from('tipos_venda')
        .select('id, id_tipo_movimentacao_nomus, nome, ativo')
        .order('id_tipo_movimentacao_nomus', { ascending: true }));
      return linhas.map((l) => ({ id: l.id, idNomus: l.id_tipo_movimentacao_nomus, nome: l.nome, ativo: l.ativo }));
    },

    async listarPapeis() {
      return conferir(await cliente
        .from('papeis')
        .select('codigo, label, descricao, home, ordem, builtin, papel_escopos(dominio, escopo), papel_permissoes(acao, permitido)')
        .order('ordem', { ascending: true }));
    },

    async listarUsuarios() {
      return conferir(await cliente
        .from('profiles')
        .select('id, nome, iniciais, email, telefone, papel, ativo, id_pessoa_vendedor, ultimo_acesso_em')
        .is('deleted_at', null)
        .order('nome', { ascending: true }));
    },

    // Telefone/WhatsApp do consultor (0078). `.select('id')` para saber se a
    // linha foi gravada: sem permissao, a RLS devolve zero linhas SEM erro, e
    // a tela diria "salvo" para uma gravacao que nao aconteceu.
    async atualizarTelefoneUsuario(id, telefone) {
      const linhas = conferir(await cliente.from('profiles').update({ telefone: telefone || null }).eq('id', id).select('id'));
      if (!linhas?.length) throw Object.assign(new Error('Sem permissão para alterar este usuário.'), { code: '42501' });
      return linhas[0];
    },

    // Cada pessoa edita o PROPRIO nome, iniciais e telefone (levantamento de 02/10: "Meu perfil" so mexia na memoria).
    // O banco ja permite so isto: `grant update (nome, iniciais, avatar_url)` (0034) e `(telefone)` (0078), e a politica
    // `profiles_upd_self` limita a propria linha. Cargo e e-mail NAO tem grant (a escalada de 25/08 segue fechada).
    // Zero linhas = sem permissao: a RLS nao da erro ao esconder a linha, e a tela diria "salvo" sem ter salvo.
    async salvarMeuPerfil(id, { nome, iniciais, telefone }) {
      if (!String(nome ?? '').trim()) throw Object.assign(new Error('O nome não pode ficar em branco.'), { code: 'nome_vazio' });
      const linhas = conferir(await cliente
        .from('profiles')
        .update({ nome: String(nome).trim(), iniciais: String(iniciais ?? '').trim() || null, telefone: String(telefone ?? '').trim() || null })
        .eq('id', id)
        .select('id, nome, iniciais, telefone'));
      if (!linhas?.length) throw Object.assign(new Error('Sem permissão para alterar este perfil.'), { code: '42501' });
      return linhas[0];
    },

    // ===== Parte 2 de cargos-como-dados (migration 0080) =====
    // Aprovada pelo Kaio em 28/09 (opcao A, "so o admin administra cargos e
    // usuarios"). Cargo, ativo e vendedor do Nomus -- e os cargos em si -- so
    // gravam por estas funcoes, que conferem na primeira linha se quem chama e
    // Admin ativo. profiles.papel segue sem grant de gravacao: a escalada de
    // 25/08. `.rpc()` aqui e a excecao prevista no CLAUDE.md para esta parte.

    // Grava os tres campos de uma vez: o vendedor do Nomus nulo LIMPA, entao
    // quem chama manda sempre o valor atual dele.
    async definirUsuario(id, { papel, ativo, idPessoaVendedor }) {
      conferir(await cliente.rpc('admin_definir_usuario', {
        p_user: id, p_papel: papel ?? null, p_ativo: ativo ?? null,
        p_id_pessoa_vendedor: idPessoaVendedor ?? null,
      }));
    },

    // Exclusao logica (0101): o perfil ganha `deleted_at` e perde o acesso na
    // hora. Nao e DELETE: apagar a linha levaria comissoes e metas em cascata.
    // O `code` do banco (42501, 22023, PT404) sobe pelo `conferir` para a tela
    // mostrar a mensagem certa.
    async excluirUsuario(id) {
      conferir(await cliente.rpc('admin_excluir_usuario', { p_user: id }));
    },

    // Escopos e permissoes: so as chaves enviadas mudam (upsert no banco).
    async salvarCargo(codigo, { label, descricao, home, escopos, permissoes }) {
      conferir(await cliente.rpc('admin_salvar_cargo', {
        p_codigo: codigo, p_label: label, p_descricao: descricao ?? null, p_home: home ?? null,
        p_escopos: escopos || {}, p_permissoes: permissoes || {},
      }));
    },

    async excluirCargo(codigo) {
      conferir(await cliente.rpc('admin_excluir_cargo', { p_codigo: codigo }));
    },

    // ===== Tabelas de preço =====
    //
    // As 23 linhas de `tabelas_precos` sao REAIS (vieram do seed 0004, espelhando
    // o ERP). A tela, ate 16/09, ignorava todas e mostrava QUATRO INVENTADAS de
    // src/data.jsx. Quem escolhesse uma no cadastro do cliente recebia
    // `22P02 invalid input syntax for type uuid: "tp_premium"` -- medido -- porque
    // o id da constante nem uuid era.
    //
    // So as ativas: as inativas do ERP (Setembro 2022, MARCO 2024, e as quatro
    // inventadas que ficaram no banco) nao devem ser oferecidas para escolha.
    async listarTabelasPreco() {
      return conferir(await cliente
        .from('tabelas_precos')
        .select('id, codigo, nome, descricao, tipo, fator, desconto_base, cor, id_tabela_preco_nomus, ativo')
        .eq('ativo', true)
        .order('tipo', { ascending: true })
        .order('fator', { ascending: true, nullsFirst: false })
        .order('nome', { ascending: true }));
    },

    // Itens de uma tabela MANUAL (0058). Só `TABELA PAULA` e `Especial
    // Fortaleza` são manuais -- 31 itens ao todo --, então a consulta é pequena
    // e sob demanda. Tabela de FATOR não tem itens: o preço dela é calculado.
    async listarItensTabelaPreco(tabelaId) {
      if (!tabelaId) return [];
      return conferir(await cliente
        .from('tabela_preco_itens')
        .select('id, produto_id, produto_nome, unidade, preco_lista, preco_tabela')
        .eq('tabela_id', tabelaId)
        .order('produto_nome', { ascending: true }));
    },

    // ===== Metas =====
    //
    // Ate 22/09 as metas eram numeros fixos no codigo (empresa R$ 1,4 mi,
    // individual R$ 300 mil), e o "Salvar metas" so mostrava um toast. A tabela
    // existe desde a 0010 com a RLS certa: todos leem as metas de empresa e
    // funil (profile_id nulo) e a PROPRIA meta individual; so gestor grava
    // (metas_mod). Nada a mudar no banco -- faltava a tela usar.
    async listarMetas() {
      return conferir(await cliente
        .from('metas')
        .select('id, escopo, funil_id, profile_id, papel, competencia, metrica, valor_meta')
        .order('competencia', { ascending: true }));
    },

    async gravarMeta(alvo) {
      return gravarMetaNoBanco(alvo);
    },

    // ===== Envolvidos e convites (0063/0066) =====
    //
    // Vincular so insere a linha: o STATUS e o banco que decide (gatilho da
    // 0066) -- conflito na agenda -> pendente, livre -> aceito. O que o cliente
    // mandar em `status` e ignorado, de proposito.
    async vincularEnvolvidos(tarefaId, userIds) {
      if (!userIds?.length) return [];
      return conferir(await cliente.from('tarefa_envolvidos')
        .insert(userIds.map((user_id) => ({ tarefa_id: tarefaId, user_id })))
        .select('user_id, status'));
    },

    async desvincularEnvolvidos(tarefaId, userIds) {
      if (!userIds?.length) return [];
      return conferir(await cliente.from('tarefa_envolvidos')
        .delete().eq('tarefa_id', tarefaId).in('user_id', userIds).select('user_id'));
    },

    // Livre/ocupado de UM colega numa janela. Primeiro `.rpc()` da tela, aprovado
    // pelo Kaio (caminho A, spec da agenda): a funcao e security definer porque
    // a RLS esconde a agenda alheia, e devolve so a FAIXA -- nunca o conteudo.
    async consultarAgenda(userId, inicio, fim) {
      const iso = (d) => (d instanceof Date ? d : new Date(d)).toISOString();
      return conferir(await cliente.rpc('agenda_conflito', { p_user: userId, p_inicio: iso(inicio), p_fim: iso(fim) }));
    },

    // Os convites de quem esta logado, para o sino saber o que ainda pede
    // resposta. A RLS (0063) ja so devolve as linhas do proprio usuario ou das
    // tarefas que ele edita; o filtro explicito recorta so as dele.
    async listarMeusConvites(meuId) {
      if (!meuId) return [];
      return conferir(await cliente.from('tarefa_envolvidos')
        .select('tarefa_id, status, respondido_em').eq('user_id', meuId));
    },

    // Aceitar ou recusar. Zero linhas atualizadas NAO e sucesso: a RLS de
    // update (so o proprio participante) recusa em silencio -- o PostgREST nao
    // da erro, so nao atualiza nada. Mesma armadilha medida nas metas (22/09).
    // UPDATE que a RLS barra devolve 0 linhas SEM erro (0070: so quem tem
    // produtos.edit). Sem esta traducao a tela diria "salvo" sem ter salvo.
    async atualizarProduto(id, patch) {
      const dbPatch = produtoPatchParaBanco(patch);
      const linhas = conferir(await cliente.from('produtos')
        .update(dbPatch).eq('id', id).select('id, descricao_estendida, dimensoes, peso_kg'));
      if (!linhas.length) {
        const falha = new Error('Seu cargo não pode editar produtos.');
        falha.code = '42501';
        throw falha;
      }
      const l = linhas[0];
      return { id: l.id, descricaoEstendida: l.descricao_estendida || '', dimensoes: l.dimensoes || '', pesoKg: l.peso_kg ?? null };
    },

    async responderConvite(tarefaId, meuId, status) {
      const linhas = conferir(await cliente.from('tarefa_envolvidos')
        .update({ status, respondido_em: new Date().toISOString() })
        .eq('tarefa_id', tarefaId).eq('user_id', meuId).select('tarefa_id, status'));
      if (!linhas.length) {
        const falha = new Error('Convite não encontrado ou sem permissão para respondê-lo.');
        falha.code = '42501';
        throw falha;
      }
      return linhas[0];
    },

    // ===== Tarefas (a agenda) =====
    //
    // Ate 21/09 a agenda era 100% memoria: `ALERT_SEEDS` vazio, `createAlert`
    // so no `setState`. O vendedor agendava o follow-up combinado com o
    // cliente, a tela confirmava, e o F5 apagava o compromisso. A tabela, a
    // RLS e o GRANT existem desde a 0008/0022 -- faltava a tela usar.
    //
    // SEM filtro explicito por destinatario, ao contrario do sino: a RLS
    // (0022:212) ja devolve so autor/responsavel, e a agenda de gestor DEVE
    // mesmo trazer a equipe -- `scopeAlerts` decide o recorte na tela.
    async listarTarefas() {
      return lerEmPaginas(() => cliente
        .from('tarefas')
        .select('id, titulo, descricao, negocio_id, cliente_id, quando, inicio, fim, canal, prioridade, feito, feito_em, autor_id, responsavel_id, created_at, subtarefas(id, texto, feito, ordem), tarefa_envolvidos(user_id, status, respondido_em)', { count: 'exact' })
        .is('deleted_at', null)
        .order('quando', { ascending: true })
        .order('id', { ascending: true }), { oQue: 'a agenda' });
    },

    // Subtarefas entram no MESMO passo da tarefa. Duas chamadas separadas
    // deixariam a tarefa gravada e o checklist perdido se a segunda falhasse --
    // e a tela nao teria como saber qual das duas foi.
    async criarTarefa(linha, subtarefas = []) {
      const tarefa = conferir(await cliente.from('tarefas').insert(linha).select().single());
      if (subtarefas.length) {
        const linhas = subtarefas.map((st, i) => ({ tarefa_id: tarefa.id, texto: st.label, feito: !!st.done, ordem: i }));
        tarefa.subtarefas = conferir(await cliente.from('subtarefas').insert(linhas).select());
      }
      return tarefa;
    },

    async atualizarTarefa(id, dbPatch) {
      return conferir(await cliente.from('tarefas').update(dbPatch).eq('id', id).select().single());
    },

    async criarSubtarefa(tarefaId, texto, ordem = 0) {
      return conferir(await cliente.from('subtarefas')
        .insert({ tarefa_id: tarefaId, texto, feito: false, ordem }).select().single());
    },

    async atualizarSubtarefa(id, patch) {
      return conferir(await cliente.from('subtarefas').update(patch).eq('id', id).select().single());
    },

    // ===== Anexos do negocio (Supabase Storage, bucket privado `anexos`; migration 0098) =====
    //
    // O arquivo vai para o Storage e os metadados para a tabela `anexos`. As duas partes sao conferidas pelo banco como quem chama
    // (RLS do Storage e da tabela): so quem ENXERGA o negocio lista, envia e baixa. A lista pede `deleted_at is null`: o SELECT nao
    // esconde as apagadas porque o UPDATE exige que a linha nova passe nele (nota da migration 0098).
    async listarAnexos(negocioId, nomePorId = {}) {
      const linhas = conferir(await cliente
        .from('anexos')
        .select('id, nome, extensao, mime, tamanho_bytes, storage_path, paginas, uploader_id, created_at')
        .eq('negocio_id', negocioId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false }));
      return (linhas || []).map((l) => mapearAnexo(l, nomePorId));
    },

    // Envia UM arquivo. Ordem: arquivo primeiro, linha depois. Se a linha falhar o arquivo e removido, para nao sobrar orfao no bucket
    // (a politica de DELETE deixa quem enviou apagar o proprio arquivo ENQUANTO ele nao tem linha). Lanca em qualquer falha.
    async enviarAnexo(negocioId, arquivo, uploaderId, nomePorId = {}) {
      const v = validarArquivo(arquivo);
      if (!v.ok) throw new Error(v.motivo);
      const caminho = caminhoDoAnexo(negocioId, arquivo.name, globalThis.crypto.randomUUID());
      const tipo = tipoDoArquivo(arquivo);
      const subida = await cliente.storage.from(BUCKET_ANEXOS).upload(caminho, arquivo, { upsert: false, contentType: tipo });
      if (subida.error) {
        const falha = new Error(subida.error.message || 'Falha ao enviar o arquivo.');
        falha.code = subida.error.statusCode ?? subida.error.status ?? subida.error.code;
        throw falha;
      }
      const gravada = await cliente.from('anexos').insert({
        dono_tipo: 'negocio', negocio_id: negocioId, nome: arquivo.name, extensao: arquivo.name.split('.').pop().toLowerCase(),
        mime: tipo, tamanho_bytes: arquivo.size, storage_path: caminho, uploader_id: uploaderId,
      }).select('id, nome, extensao, mime, tamanho_bytes, storage_path, paginas, uploader_id, created_at').single();
      if (gravada.error) {
        // melhor esforco: se nem a limpeza passar, o gestor ainda remove o orfao; o erro que importa e o da linha
        await cliente.storage.from(BUCKET_ANEXOS).remove([caminho]).catch(() => {});
        return conferir(gravada);
      }
      return mapearAnexo(gravada.data, nomePorId);
    },

    // Link de download que vale 60 s, gerado NA HORA do clique (nunca guardado): quem tiver o link baixa, entao ele expira.
    async urlDoAnexo(storagePath, nome) {
      const { data, error } = await cliente.storage.from(BUCKET_ANEXOS).createSignedUrl(storagePath, 60, { download: nome || true });
      if (error || !data?.signedUrl) {
        const falha = new Error(error?.message || 'Não foi possível gerar o link do arquivo.');
        falha.code = error?.statusCode ?? error?.status;
        throw falha;
      }
      return data.signedUrl;
    },

    // Apagar = MARCAR `deleted_at` (reversivel; o arquivo fica no bucket). `.select()` para saber se alguma linha foi mesmo marcada:
    // a RLS do UPDATE filtra em silencio, e "0 linhas" sem erro pareceria sucesso.
    async apagarAnexo(id) {
      const linhas = conferir(await cliente.from('anexos').update({ deleted_at: new Date().toISOString() }).eq('id', id).select('id'));
      if (!linhas || linhas.length !== 1) throw new Error('O anexo não foi encontrado ou você não tem acesso a ele.');
    },

    // ===== Notificações (o sino) =====
    //
    // Até 15/09 o sino era 100% memória: a notificação nascia no `setState`,
    // vivia no máximo 60 itens e morria no F5. Quem fosse avisado de uma menção
    // e recarregasse a página perdia o aviso, e ninguém ficava sabendo que
    // perdeu. A tabela `notificacoes`, o índice e a RLS existem desde a 0013 --
    // faltava a tela usar.
    //
    // FILTRO EXPLÍCITO POR DESTINATÁRIO, e isto NÃO é redundante com a RLS:
    // `notif_sel` (0023) permite `destinatario_id = auth.uid() OR
    // util.sou_gestor()`. Sem o filtro, o sino de qualquer gestor traria as
    // notificações da equipe inteira -- a RLS está certa (gestor pode auditar),
    // mas o sino é caixa pessoal.
    async listarNotificacoes(meuId, limite = 50) {
      if (!meuId) return [];
      return conferir(await cliente
        .from('notificacoes')
        .select('id, destinatario_id, autor_id, tipo, titulo, texto, origem_tipo, origem_id, lida, created_at')
        .eq('destinatario_id', meuId)
        .order('created_at', { ascending: false })
        .limit(limite));
    },

    // `autor_id` vai preenchido porque a RLS exige: `notif_ins` aceita
    // `autor_id = auth.uid() or util.sou_gestor()`. Mandar nulo faria o insert
    // ser recusado com 42501 para quem não é gestor -- e a notificação sumiria
    // justamente para o vendedor comum.
    //
    // `devolver: false` e para o aviso a OUTRA pessoa. `insert(...).select()` pede a linha de volta, e o PostgREST so
    // a devolve se ela passa tambem pela regra de LEITURA: `notif_sel` so deixa ver a notificacao endereçada a si
    // (ou ser gestor). Para quem nao e gestor, avisar um colega tomava 42501 e o aviso sumia (descoberto em 02/10,
    // pelo repasse do SDR; valia tambem para mencao e convite de tarefa). Sem pedir a linha, a regra de INSERT
    // (`autor_id = auth.uid()`) continua valendo e nada de RLS muda. Teste: notificacao-para-outra-pessoa.test.js.
    async criarNotificacoes(linhas, { devolver = true } = {}) {
      if (!linhas?.length) return [];
      if (!devolver) {
        conferir(await cliente.from('notificacoes').insert(linhas));
        return [];
      }
      return conferir(await cliente.from('notificacoes').insert(linhas).select());
    },

    async marcarNotificacoesLidas(ids) {
      if (!ids?.length) return [];
      return conferir(await cliente
        .from('notificacoes')
        .update({ lida: true, lida_em: new Date().toISOString() })
        .in('id', ids)
        .select('id'));
    },

    // ===== Propostas (Etapa 3a) =====
    // Antes 100% memoria em store.jsx (ctrlSeq/setPropostas). Agora sob RLS:
    // propostas_cud/_sel e proposta_itens_* (schema 0007). Itens aninhados numa
    // consulta so, como listarPapeis faz com escopos/permissoes.
    async listarPropostasPorNegocio(negocioId) {
      return conferir(await cliente
        .from('propostas')
        .select(SELECT_PROPOSTA)
        .eq('negocio_id', negocioId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false }));
    },

    // Dois passos: PostgREST nao insere em duas tabelas numa chamada. Se os itens
    // falharem, a proposta fica sem itens (o vendedor edita/retenta) -- nao ha
    // .rpc()/transacao aqui por decisao do projeto. Devolve a proposta ja com os
    // itens aninhados (mesmo caminho de leitura), para o store semear do retorno.
    async criarProposta(dados, itens = []) {
      const proposta = conferir(await cliente
        .from('propostas')
        .insert(dados)
        .select().single());
      if (itens.length) {
        const comDescricao = await congelarDescricaoTecnica(itens);
        conferir(await cliente
          .from('proposta_itens')
          .insert(comDescricao.map((it, i) => ({ ...it, proposta_id: proposta.id, ordem: it.ordem ?? i }))));
      }
      return conferir(await cliente
        .from('propostas')
        .select(SELECT_PROPOSTA)
        .eq('id', proposta.id).single());
    },

    // Carga inicial: todas as propostas que o usuário pode ver, para o store
    // montar o mapa por negócio de uma vez — mesmo padrão de listarNegocios/
    // listarUsuarios. A RLS (0040) já escopa por dono do negócio, então não há
    // filtro de owner aqui: filtrar de novo no cliente só esconderia um furo de
    // RLS se algum dia ele voltasse.
    async listarPropostas() {
      return lerEmPaginas(() => cliente
        .from('propostas')
        .select(SELECT_PROPOSTA, { count: 'exact' })
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .order('id', { ascending: true }), { oQue: 'as propostas' });
    },

    // Itens são trocados em bloco (apaga e reinsere), não item a item: a
    // revalidação devolve a lista inteira recalculada (src/drawer.jsx:828-837) e
    // casar linha a linha exigiria um id estável que a tela não tem. DELETE
    // físico aqui é correto — o histórico que importa é o da proposta, e item
    // órfão de uma revalidação antiga só confundiria o total.
    async substituirItensProposta(propostaId, itens = []) {
      conferir(await cliente
        .from('proposta_itens')
        .delete().eq('proposta_id', propostaId));
      if (itens.length) {
        // Mesmo congelamento da criacao: revisar a proposta reescreve os itens,
        // e a descricao tem de vir junto. Sem isto, editar uma proposta APAGARIA
        // a descricao tecnica dela.
        const comDescricao = await congelarDescricaoTecnica(itens);
        conferir(await cliente
          .from('proposta_itens')
          .insert(comDescricao.map((it, i) => ({ ...it, proposta_id: propostaId, ordem: it.ordem ?? i }))));
      }
      return conferir(await cliente
        .from('propostas')
        .select(SELECT_PROPOSTA)
        .eq('id', propostaId).single());
    },

    async atualizarProposta(id, patch) {
      return conferir(await cliente
        .from('propostas')
        .update(patch)
        .eq('id', id).select().single());
    },

    async atualizarStatusProposta(id, status) {
      return conferir(await cliente
        .from('propostas')
        .update({ status })
        .eq('id', id).select().single());
    },

    // Exclusao logica: a coluna deleted_at ja participa das policies e do filtro
    // de listagem. Nunca DELETE fisico -- perde a trilha e a referencia do negocio.
    async removerProposta(id) {
      return conferir(await cliente
        .from('propostas')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id).select().single());
    },
  };
}
